/* eslint-disable no-useless-escape */
import flatten from 'flat';
import merge from 'lodash/merge';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import isInteger from 'lodash/isInteger';
import findIndex from 'lodash/findIndex';
import uniqBy from 'lodash/uniqBy';
import maxBy from 'lodash/maxBy';
import filter from 'lodash/filter';
import remove from 'lodash/remove';
import isArray from 'lodash/isArray';
import moment from 'moment';
import initialState from 'config/initialLoanState';
import store from 'router/store';
import * as actionTypes from 'actions/actionTypes';
import {
  addStepToLoanForm,
  b64DecodeUnicode,
  getIndexStepByName,
  getInitialProductPrincipal,
  isBrokerChannel,
  isPayLaterChannel,
  removeNonDigitCharacters,
  toCents,
} from 'utils/helpers';
import queryStringParser from 'utils/queryStringParser';
import {
  finpassToAioBanksLT,
  possiblePeriodsCL,
  residenceDocumentTypes,
  savingTablePeriods,
  workingStateTypes,
} from 'consts';
import pullAt from 'lodash/pullAt';
import { CREDIT_APPLICATION_STATE_PENDING } from 'consts/loan/creditApplicationStates';
import {
  CREDIT_REJECTION_BDC_SCORING_NEGATIVE,
  CREDIT_REJECTION_CODE,
  CREDIT_REJECTION_EXT_SCORE_CUT,
} from 'consts/loan/responseStatusCodes';
import {
  STEP_CUSTOMER,
  STEP_INFORMATION,
  STEP_LOAN_PURPOSE,
  STEP_PROVIDENT_PRODUCT,
  STEP_PSYCHOMETRIC,
  STEP_REIDENTIFY_CUSTOMER,
  STEP_SUBMIT,
  STEP_SYSTEM_AUTHORITY,
} from 'consts/loan/steps';
import { TAX_AUTHORITY_CONSENTS_CLICKED } from 'consts/loan/localStorage';
import { getStepAfterProvidentScoring } from 'utils/scoring';
import router from 'react-router/lib/Router';
import { CENTERLINK_STATUS } from 'consts/loan/centerlinkStatuses';
import { PRODUCT_TYPES } from 'consts/products';

function putInfoCrediteaState(state, action) {
  const newState = {
    ...state,
    isLoanSubmitting: true,
  };

  if (COUNTRY === 'MX') {
    const { data } = action;
    newState.customer.email = data.email;
    if (data.postalCode) {
      newState.customer.address = {
        city: data.city,
        door: data.internalNumber,
        municipality: data.municipality,
        neighbourhood: data.neighbourhood,
        postcode: data.postalCode,
        province: data.province,
        street: `${data.street}, ${data.externalNumber}`,
      };
    }
    if (data.firstName) {
      newState.customer.firstName = data.firstName;
    }
    if (data.lastName) {
      newState.customer.lastName = data.lastName;
    }
    if (data.secondFirstName) {
      newState.customer.secondFirstName = data.secondFirstName;
    }
    if (data.secondLastName) {
      newState.customer.secondLastName = data.secondLastName;
    }

    newState.financialData = {
      ...newState.financialData,
      residence: data.residentialStatus,
      netIncome: toCents(removeNonDigitCharacters(data.netIncome)),
      maritalStatus: data.maritalStatus,
      education: data.education,
      company: data.company,
      occupation: data.occupation,
      employmentDuration: data.employmentDuration,
      activityType: data.activityType,
      industry: data.industry,
      retirementPensionedBy: data.retirementPensionedBy,
      studiesAtInstitution: data.studiesAtInstitution,
      studiesAtOtherInstitution: data.studiesAtOtherInstitution,
      freelancersIndustry: data.freelancersIndustry,
      loanPurpose: data.loanPurpose,
    };
  }

  if (COUNTRY === 'ES' && PARTNER_PORTAL) {
    const {
      firstName,
      lastName,
      secondLastName,
      birthDay,
      birthMonth,
      birthYear,
      countryOfBirth,
      gender,
      street,
      streetNumber,
      floor,
      door,
      postcode,
      city,
      province,
      msisdn2,
      residence,
      netIncome,
      maritalStatus,
      education,
      occupation,
      employmentDuration,
      channel,
    } = action.data;

    newState.customer = {
      ...newState.customer,
      firstName,
      lastName,
      secondLastName,
      gender,
      countryOfBirth,
      msisdn2,
      dateOfBirth: [birthYear, birthMonth, birthDay],
      address: {
        street,
        streetNumber,
        floor,
        door,
        postcode,
        city,
        province,
      },
    };
    newState.financialData = {
      ...newState.financialData,
      residence,
      maritalStatus,
      education,
      occupation,
      employmentDuration,
      channel,
      netIncome: toCents(removeNonDigitCharacters(netIncome)),
    };
  }

  return newState;
}

const stateWithLoanSubmitting = (state, isLoanSubmitting = false) => ({
  ...state,
  isLoanSubmitting,
});

const stateWithContentLoading = (state, isContentLoading = false) => ({
  ...state,
  isContentLoading,
});

const stateWithoutLoading = (state) => ({
  ...state,
  isLoanSubmitted: false,
  isLoanSubmitting: false,
  isContentLoading: false,
  isCalculatingMMP: false,
});

const stateAfterRejection = (error) => {
  const state = {
    isLoanSubmitting: false,
    rejected: error && error.statusCode ? error.statusCode : 'default',
    rejectedMessage: error && error.message ? error.message : null,
    rejectedParams: error && error.params ? error.params : {},
  };
  if (COUNTRY === 'ES') {
    state.rejectedParams.fallbackPartner = Math.random() >= 0.5 ? 'iahorro' : 'solcredito';
  }
  if (COUNTRY === 'MX') {
    state.rejected = error && error.params ? error.params.rejectReason : 'default';
  }
  return state;
};

function stateForNextStepWithLoanSubmitting(state, isLoanSubmitting = false) {
  return {
    ...stateWithLoanSubmitting(state, isLoanSubmitting),
    currentStep: state.currentStep + 1,
  };
}

const transformFirstDrawAmountsInProductAU = (product, fieldsConfig) => {
  const { nameFirstDrawAmounts, nameFirstDrawAmount } = fieldsConfig;
  const firstDrawAmounts = product[nameFirstDrawAmounts].reduce((result, firstDrawAmount) => ({
    ...result,
    [removeNonDigitCharacters(firstDrawAmount[nameFirstDrawAmount])]: firstDrawAmount,
  }), {});
  return {
    ...product,
    [nameFirstDrawAmounts]: firstDrawAmounts,
    firstDrawAmountIndexes: Object.keys(firstDrawAmounts).sort((a, b) => (a - b)),
  };
};

const parseProductsAU = (products, productType) => {
  let fieldsConfig = {
    namePrincipal: 'creditAmount',
    nameFirstDrawAmounts: 'withdraws',
    nameFirstDrawAmount: 'withdrawAmount',
  };
  if (productType === 'INSTALLMENT') {
    fieldsConfig = {
      namePrincipal: 'loanAmount',
      nameFirstDrawAmounts: 'productOffers',
      nameFirstDrawAmount: 'duration',
    };
  }
  const { namePrincipal } = fieldsConfig;
  return products.reduce((result, product) => ({
    ...result,
    [product[namePrincipal]]: transformFirstDrawAmountsInProductAU(product, fieldsConfig),
  }), {});
};

export default function loanReducer(state = initialState, action) {
  if (DEBUG) {
    console.log('reducers action: ', action);
  }

  switch (action.type) {
    case actionTypes.RESTORE_SESSION_FAIL: {
      const { status } = action.error;
      return {
        ...state,
        isServiceUnavailable: (status >= 500 && status <= 504) || (status === 413),
      };
    }

    case actionTypes.SET_CREDIT_APPLICATION_ID:
      return {
        ...state,
        creditApplicationId: action.data,
      };

    case actionTypes.SET_CUSTOMER_EMAIL:
      return {
        ...state,
        customer: {
          ...state.customer,
          email: action.data,
        },
      };

    case actionTypes.SET_LEGACY_BALANCE:
      return {
        ...state,
        legacyBalance: action.data,
      };

    case actionTypes.SET_BROKER_APP:
      return {
        ...state,
        isFilledByBroker: true,
      };

    case actionTypes.SET_BROKER_URL:
      return {
        ...state,
        brokerUrl: action.data,
      };

    case actionTypes.RESET: {
      const nextState = { ...initialState };
      if (COUNTRY === 'ES') {
        nextState.libertoSucceeded = false;
      }
      if (COUNTRY === 'AU') {
        nextState.bankScrapingResult = {};
      }
      return nextState;
    }

    case actionTypes.RESTORE: {
      const nextState = {
        ...stateWithoutLoading(state),
      };
      if (COUNTRY === 'LV' && VARIANT_B) {
        const { loan } = action.data;
        if (loan) {
          const { selectedProduct, canChangeLimit } = loan;
          const { selectedMaturity, selectedMMP, firstDrawAmount } = selectedProduct;

          Object.assign(nextState, {
            selectedProduct: {
              ...nextState.selectedProduct,
              selectedMaturity,
              selectedMMP,
              firstDrawAmount,
            },
            canChangeLimit,
          });
        }
      } else {
        Object.assign(nextState, action.data.loan);
      }

      if (COUNTRY === 'EE') {
        const { authentication: { customerRegistered } } = store.getState();
        if (customerRegistered) {
          nextState.steps = state.steps;
        }
      }

      return nextState;
    }

    case actionTypes.INIT_LOAN_STEP:
      return {
        ...stateWithLoanSubmitting(state),
        currentStep: action.data,
      };

    case actionTypes.SET_STEP:
      return {
        ...state,
        currentStep: action.data,
      };

    case actionTypes.STEP_BACK: {
      let decrement = 1;
      if (COUNTRY === 'LV' && INSTANTOR_ENABLED) {
        const oneCentIndex = state.steps.findIndex((item) => item.type === 'ONE_CENT');
        if (state.verifyOptions && state.verifyOptions === 'onecent' && state.currentStep === oneCentIndex) {
          decrement = 2;
        }
      }
      if (COUNTRY === 'EE') {
        const { currentStep, steps } = state;
        const loanPurposeStepIndex = getIndexStepByName(steps, 'LOAN_PURPOSE');
        const sustemAuthorityStepIndex = getIndexStepByName(steps, STEP_SYSTEM_AUTHORITY.type);

        if (currentStep === loanPurposeStepIndex && currentStep - 1 === sustemAuthorityStepIndex) {
          decrement = 2;
        }
      }
      if (state.currentStep > 0) {
        return {
          ...state,
          currentStep: state.currentStep - decrement,
        };
      }
      return state;
    }

    case actionTypes.GO_TO_CREDIT_LIMIT: {
      return {
        ...state,
        currentStep: state.currentStep + 1,
        isLoanSubmitting: false,
        isLoadingMMPbyMonth: false,
      };
    }

    case actionTypes.REDIRECT_LOGIN: {
      return {
        ...state,
        verificationCode: null,
      };
    }

    case actionTypes.PUT_REPAYMENT_PLAN_SUCCESS: {
      const nextState = {
        ...stateForNextStepWithLoanSubmitting(state),
        repaymentPlan: {
          ...state.repaymentPlan,
          enabled: true,
        },
      };
      nextState.currentStep -= 1;
      return nextState;
    }

    case actionTypes.PUT_CALMA_SUCCESS:
      return {
        ...stateWithLoanSubmitting(state, false),
        calma: {
          ...state.calma,
          enabled: action.data.calma === 'yes',
        },
        calmaPassed: true,
        currentStep: state.currentStep + 1,
        isPinValidationLoading: false,
        otpSectionVisible: false,
      };

    case actionTypes.SET_CZ_INCOME_VERIFICATION_WAY:
      return {
        ...state,
        incomeVerificationWay: action.data.incomeVerificationWay,
      };

    case actionTypes.SKIP_CZ_INCOME_VERIFICATION:
      return {
        ...state,
        incomeVerificationWay: action.data.incomeVerificationWay,
      };

    case actionTypes.SET_CZ_IDENTITY_VERIFICATION_WAY:
      return {
        ...state,
        identityVerificationWay: action.data.identityVerificationWay,
      };

    case actionTypes.SKIP_CZ_IDENTITY_VERIFICATION:
      return {
        ...state,
        identityVerificationWay: action.data.identityVerificationWay,
        isLoanSubmitted: true,
      };

    case actionTypes.PUT_CHANGE_PRODUCT_SUCCESS: {
      const nextState = {
        ...stateWithLoanSubmitting(state, false),
        isLoanSubmitted: false,
        currentStep: state.currentStep - 1,
      };

      return nextState;
    }

    case actionTypes.SUBMIT:
      return {
        ...stateWithLoanSubmitting(state, true),
        isLoanSubmitted: false,
      };

    case actionTypes.SUBMIT_SUCCESS:
      return {
        ...stateWithLoanSubmitting(state, false),
        isLoanSubmitted: true,
      };

    case actionTypes.GET_CONTRACT_INTEREST_VALUE:
      return {
        ...state,
        ...stateWithLoanSubmitting(state, true),
      };

    case actionTypes.GET_CONTRACT_INTEREST_VALUE_SUCCESS:
      return {
        ...state,
        product: {
          ...state.product,
          interestValue: action.result,
        },
      };

    case actionTypes.GET_CONTRACT_INTEREST_VALUE_FAIL:
      return {
        ...state,
        ...stateAfterRejection(action.data),
      };

    case actionTypes.GET_CREDIT_APPLICATION_LOAN:
    case actionTypes.INIT_LOAN:
      return {
        ...stateWithLoanSubmitting(state, true),
        isServiceUnavailable: false,
      };

    case actionTypes.CREATE_CREDIT_APPLICATION_AFTER_REIDENTIFY_SUCCESS:
    case actionTypes.GET_CREDIT_APPLICATION_LOAN_SUCCESS:
    case actionTypes.INIT_CREDIT_APPLICATION_SUCCESS: {
      const loan = isArray(action.result) ? action.result[0].data : action.result.data;
      const {
        channel, documentRequirements, financialData, firstDrawAmount, id, product,
      } = loan;
      const isFilledByBroker = (
        (financialData && isBrokerChannel(financialData.channel))
        || isBrokerChannel(channel)
      );
      const steps = [].concat(initialState.steps);
      const selectedProduct = product || {};
      selectedProduct.firstDrawAmount = firstDrawAmount;
      const isLoanSubmitted = (COUNTRY === 'FI' || COUNTRY === 'AU') && loan.state === CREDIT_APPLICATION_STATE_PENDING;
      const nextState = {
        ...stateWithLoanSubmitting(state, false),
        isFilledByBroker,
        isServiceUnavailable: false,
        state: loan.state,
        id,
        documentRequirements,
        selectedProduct,
        isLoanSubmitted,
        channel,
      };

      if (EMBEDDED) {
        nextState.financialData.channel = channel;
      }

      nextState.steps = steps;

      return nextState;
    }
    case actionTypes.INIT_LOAN_SUCCESS: {
      const loan = isArray(action.result) ? action.result[0].data : action.result.data;
      const { channel } = loan;
      const isFilledByBroker = (
        (loan.financialData && isBrokerChannel(loan.financialData.channel))
        || isBrokerChannel(channel)
      );
      const isFilledByBrokerPayLater = (
        (loan.financialData && isPayLaterChannel(loan.financialData.channel))
        || isPayLaterChannel(channel)
      );

      const steps = [].concat(initialState.steps);
      const selectedProduct = loan.product || {};
      selectedProduct.firstDrawAmount = loan.firstDrawAmount;

      const nextState = {
        ...stateWithLoanSubmitting(state, true),
        isFilledByBroker,
        isFilledByBrokerPayLater,
        isServiceUnavailable: false,
        state: loan.state,
        id: loan.id,
        documentRequirements: loan.documentRequirements,
        selectedProduct,
        isLoanSubmitted: (
          COUNTRY === 'FI'
          || COUNTRY === 'AU'
          || (COUNTRY === 'ES' && (EMBEDDED || PARTNER_PORTAL))
        ) && loan.state === 'PENDING',
        channel,
        areAdditionalQuestionsAnswered: !!loan.metadata.areAdditionalQuestionsAnswered,
        metadata: loan.metadata,
      };

      if (COUNTRY === 'AU') {
        if (INCREASE_APP) {
          const { creditApplication } = action.result[1].data;
          nextState.financialData = {
            ...creditApplication.financialData,
            ...loan.financialData,
          };
          nextState.productType = creditApplication.product.type;
        } else {
          nextState.financialData = loan.financialData;
        }
        nextState.applicationRef = loan.applicationRef;

        const isGovernmentBenefits = loan.financialData?.GOVERNMENT_BENEFITS;
        const isSACC = loan.product?.name.includes(PRODUCT_TYPES.SACC);
        nextState.shouldShowSACCPage = isGovernmentBenefits && isSACC;
      }

      if (EMBEDDED) {
        if (!action.data.isChannelDefault) {
          nextState.financialData.channel = action.data.channel;
        }
      } else {
        nextState.financialData.channel = action.data.channel;
      }

      nextState.steps = steps;

      return nextState;
    }

    case actionTypes.INIT_LOAN_FAIL: {
      const { status, data = {} } = action.error;
      const { statusCode } = data;

      if (
        (COUNTRY === 'FI' && status === 412)
        || statusCode === 'CREDIT_REJECTION'
      ) {
        return {
          ...state,
          ...stateAfterRejection(data),
        };
      }
      return {
        ...stateWithLoanSubmitting(state, false),
        isServiceUnavailable: (status >= 502 && status <= 504) || status === 413 || status === 403,
      };
    }

    case actionTypes.INIT_CREDIT_APPLICATION_FAIL:
    case actionTypes.GET_CREDIT_APPLICATION_LOAN_FAIL: {
      const { status, data = {} } = action.error;
      if (status !== 404) {
        const { statusCode } = data;

        if (
          (COUNTRY === 'FI' && status === 412)
          || statusCode === CREDIT_REJECTION_CODE
        ) {
          return {
            ...state,
            ...stateAfterRejection(data),
          };
        }

        const isServiceUnavailable = (status >= 502 && status <= 504)
          || status === 413
          || status === 403;

        return {
          ...stateWithLoanSubmitting(state, false),
          isServiceUnavailable,
        };
      }
      return state;
    }

    case actionTypes.RESTORE_SESSION_SUCCESS: {
      const { customer, authenticationMethod } = action.result.data;

      if (COUNTRY === 'AU' && customer) {
        const { address } = customer;

        const hasAnyAddressData = Object.values(address).some((value) => !!value);

        return {
          ...state,
          areAddressDetailsHidden: !hasAnyAddressData,
          customer: {
            ...customer,
            medicare: {
              ...state.medicare,
              cardColour: customer.metadata.CARD_COLOUR,
            },
            address: {
              residentialStatus: address.residentialStatus,
              city: address.city,
              street: address.street,
              unitNumber: address.door,
              streetNumber: address.floor,
              state: address.province,
              postCode: address.postcode,
            },
          },
        };
      }

      if (COUNTRY === 'ES') {
        if (authenticationMethod === 'IMPERSONATION' && customer.metadata.broker_data_validation === 'false') {
          return {
            ...state,
            missingFields: customer.metadata.broker_missing_data.split(', '),
          };
        }
      }

      return state;
    }

    case actionTypes.GET_REGISTER_DATA: {
      return {
        ...stateWithContentLoading(state, true),
        isRegisterDataLoading: true,
      };
    }

    case actionTypes.GET_REGISTER_DATA_SUCCESS: {
      const loan = action.result.data;
      const storeState = store.getState();
      if (state.state === 'PENDING' && !loan.pendingIdentificationRequest
        && storeState.authentication.identificationState === 'IDENTIFIED'
        && loan.documentRequirements.length === 0) {
        return {
          ...state,
          isLoanSubmitted: true,
          isContentLoading: false,
          isRegisterDataLoading: false,
        };
      }
      if (state.state === 'PENDING' && loan.pendingIdentificationRequest) {
        let pageToOpen = 'ONE_CENT';
        if (COUNTRY === 'LV' && INSTANTOR_ENABLED && state.steps.findIndex((item) => item.type === 'ONE_CENT')
          !== state.currentStep) {
          pageToOpen = 'VERIFY';
        }
        return {
          ...stateWithContentLoading(state, false),
          currentStep: state.steps.findIndex((item) => item.type === pageToOpen),
          isRegisterDataLoading: false,
        };
      }
      return {
        ...stateWithContentLoading(state, false),
        isRegisterDataLoading: false,
      };
    }

    case actionTypes.GET_REGISTER_DATA_FAIL: {
      return {
        ...stateWithContentLoading(state, false),
        isRegisterDataLoading: false,
      };
    }

    case actionTypes.PUT_CUSTOMER:
    case actionTypes.PUT_POST_OFFICE:
    case actionTypes.PUT_PRODUCT:
    case actionTypes.PUT_CHANGE_PRODUCT:
    case actionTypes.PUT_HYBRID_PRODUCT:
    case actionTypes.PUT_ADDRESS:
    case actionTypes.PUT_BANK_ACCOUNT:
    case actionTypes.PUT_CUSTOMER_PL_AGREEMENTS:
    case actionTypes.PUT_MARITAL_STATUS_PL:
    case actionTypes.PUT_CALMA:
    case actionTypes.POST_LOAN_PURPOSES:
    case actionTypes.VERIFY_LOAN_PURPOSES:
    case actionTypes.PUT_REFERENCE:
    case actionTypes.PUT_CREDENTIALS:
    case actionTypes.PUT_FAMILY_MX:
    case actionTypes.PUT_EDUCATION_WORK_MX:
    case actionTypes.POST_REGISTRATION_DETAILS:
    case actionTypes.VAS_CONFIRM:
    case actionTypes.PUT_MEDICARE:
    case actionTypes.PUT_BILL_PROTECTOR:
    case actionTypes.PUT_CONTRACT_DETAILS:
    case actionTypes.PUT_CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION:
    case actionTypes.PUT_NORDIGEN_CONSENT:
    case actionTypes.PUT_NORDIGEN_REQUEST_ID:
    case actionTypes.SIGNUP_IMPERSONATION_COMPLETE:
    case actionTypes.PATCH_GUARANTOR_THEN_FEES_ES:
    case actionTypes.PUT_MIGRATION_PRODUCT:
    case actionTypes.POST_NORDIGEN_BANK:
      return stateWithLoanSubmitting(state, true);

    case actionTypes.PUT_LOAN_PURPOSE:
      if (COUNTRY === 'ES') {
        return stateWithLoanSubmitting({
          ...state,
          financialData: {
            ...state.financialData,
            appliedForAmount: action.data.appliedForAmount,
          },
        }, true);
      }
      return stateWithLoanSubmitting(state, true);

    case actionTypes.PUT_CUSTOMER_PL_AGREEMENTS_SUCCESS: {
      return {
        ...stateForNextStepWithLoanSubmitting(state, false),
        communicationSettings: {
          ...state.communicationSettings,
          ...action.data,
        },
      };
    }

    case actionTypes.FINISH_REIDENTIFY_CUSTOMER_SUCCESS:
    case actionTypes.PUT_CUSTOMER_SUCCESS: {
      const { channel, ...rest } = action.data;
      const result = {
        ...stateWithLoanSubmitting(state, false),
        currentStep: state.currentStep + 1,
        customer: merge(
          state.customer,
          rest
        ),
      };

      if (COUNTRY === 'EE' && channel) {
        result.financialData.channel = channel;
      }

      return result;
    }

    case actionTypes.PUT_FAMILY_CREDITEA: {
      return {
        ...stateWithLoanSubmitting(state, true),
        finalState: false,
      };
    }

    case actionTypes.PUT_POST_OFFICE_SUCCESS: {
      const data = JSON.parse(action.result.config.data);
      return {
        ...stateWithLoanSubmitting(state, false),
        currentStep: state.currentStep + 1,
        ...data,
      };
    }

    case actionTypes.REPAYMENT_PLAN_AVAILABILITY_SUCCESS: {
      const repaymentPlan = action.result.data.REPAYMENT_PLAN;
      const nextState = {
        ...stateWithContentLoading(state, false),
        repaymentPlan,
      };
      return nextState;
    }

    case actionTypes.CALMA_AVAILABILITY_SUCCESS: {
      const calma = action.result.data.CALMA;
      const nextState = {
        ...stateWithContentLoading(state, false),
        calma,
      };
      if (calma && calma.isAvailable && !calma.enabled) {
        return nextState;
      }

      nextState.currentStep = state.currentStep + 1;
      // if there is no calma, set calmaPassed to True so it's skipped when reconstructing
      nextState.calmaPassed = true;

      return nextState;
    }

    case actionTypes.EXTRA_SERVICE_QUESTIONS_SUCCESS: {
      const { data: { extraServiceQuestions } } = action.result;
      return {
        ...state,
        extraServiceQuestions,
      };
    }

    case actionTypes.EXTRA_SERVICES_ADDITONAL_INFO_SUCCESS: {
      const nextState = {
        ...stateWithContentLoading(state, false),
        isExtraServicesDisabled: false,
      };

      if (state.medicare && state.medicare.isAvailable) {
        nextState.medicare = {
          ...state.medicare,
          ...action.result[0].data,
        };
      }

      if (state.billProtector && state.billProtector.isAvailable) {
        nextState.billProtector = {
          ...state.billProtector,
          ...action.result[1].data,
        };
      }
      return nextState;
    }

    case actionTypes.EXTRA_SERVICES_AVAILABILITY: {
      return {
        ...state,
        isExtraServicesDisabled: true,
      };
    }

    case actionTypes.EXTRA_SERVICES_AVAILABILITY_SUCCESS: {
      const {
        data,
        result: {
          data: { MEDICARE, BILL_PROTECTOR },
        },
      } = action;
      const { availableProducts, medicare = {}, billProtector = {} } = state;
      const index = findIndex(availableProducts, (item) => item.id === data.id);
      const selectedProduct = { ...availableProducts[index], ...data };

      const nextState = {
        ...state,
        selectedProduct,
      };

      if (MEDICARE) {
        nextState.medicare = {
          ...medicare,
          ...MEDICARE,
          selected: MEDICARE.enabled || medicare.selected,
        };
      }

      if (BILL_PROTECTOR) {
        nextState.billProtector = {
          ...billProtector,
          ...BILL_PROTECTOR,
          selected: BILL_PROTECTOR.enabled || billProtector.selected,
        };
      }

      return nextState;
    }

    case actionTypes.EXTRA_SERVICES_ADDITONAL_INFO_FAIL:
    case actionTypes.EXTRA_SERVICES_AVAILABILITY_FAIL: {
      return {
        ...stateWithContentLoading(state, false),
        isExtraServicesDisabled: false,
      };
    }

    case actionTypes.VAS_TOGGLE: {
      const { serviceName } = action.data;
      const service = state[serviceName];
      if (service) {
        return {
          ...state,
          [serviceName]: {
            ...service,
            selected: !service.selected,
          },
        };
      }
      return state;
    }

    case actionTypes.PUT_CUSTOMER_FAIL:
    case actionTypes.PUT_POST_OFFICE_FAIL:
    case actionTypes.PUT_ADDRESS_FAIL:
    case actionTypes.PUT_LOAN_PURPOSE_FAIL:
    case actionTypes.PUT_BANK_ACCOUNT_FAIL:
    case actionTypes.PUT_PRODUCT_FAIL:
    case actionTypes.PUT_CHANGE_PRODUCT_FAIL:
    case actionTypes.PUT_HYBRID_PRODUCT_FAIL:
    case actionTypes.PUT_CREDENTIALS_FAIL:
    case actionTypes.SUBMIT_FAIL:
    case actionTypes.PUT_CUSTOMER_PL_AGREEMENTS_FAIL:
    case actionTypes.PUT_MARITAL_STATUS_PL_FAIL:
    case actionTypes.PUT_REPAYMENT_PLAN_FAIL:
    case actionTypes.POST_LOAN_PURPOSES_FAIL:
    case actionTypes.VERIFY_LOAN_PURPOSES_FAIL:
    case actionTypes.PUT_REFERENCE_FAIL:
    case actionTypes.PUT_FAMILY_MX_FAIL:
    case actionTypes.PUT_FAMILY_CREDITEA_FAIL:
    case actionTypes.PUT_EDUCATION_WORK_MX_FAIL:
    case actionTypes.POST_REGISTRATION_DETAILS_FAIL:
    case actionTypes.VAS_CONFIRM_FAIL:
    case actionTypes.PUT_MEDICARE_FAIL:
    case actionTypes.PUT_BILL_PROTECTOR_FAIL:
    case actionTypes.PUT_MEDICARE_SUCCESS:
    case actionTypes.PUT_BILL_PROTECTOR_SUCCESS:
    case actionTypes.PUT_CONTRACT_DETAILS_FAIL:
    case actionTypes.PUT_REPAYMENT_PLAN:
    case actionTypes.ADDRESS_AU_FAIL:
    case actionTypes.FAMILY_AU_FAIL:
    case actionTypes.EDUCATION_WORK_FAIL:
    case actionTypes.INCOME_AU_FAIL:
    case actionTypes.EXPENSES_AU_FAIL:
    case actionTypes.LOANS_AU_FAIL:
    case actionTypes.SIGNUP_IMPERSONATION_COMPLETE_FAIL:
    case actionTypes.PATCH_GUARANTOR_THEN_FEES_ES_FAIL:
    case actionTypes.PUT_MIGRATION_PRODUCT_FAIL:
    case actionTypes.POST_NORDIGEN_BANK_FAIL:
    case actionTypes.GET_GOV_BENEFITS_VALUE_FAIL:
      return stateWithLoanSubmitting(state, false);

    case actionTypes.IDENT_DOCUMENTS_FAIL: {
      const nextState = stateWithLoanSubmitting(state, false);
      if (COUNTRY === 'AU') {
        nextState.identDocumentError = true;
      }
      return nextState;
    }

    case actionTypes.REMOVE_ACTION_ERROR: {
      return {
        ...stateWithLoanSubmitting(state, false),
        identDocumentError: false,
      };
    }

    case actionTypes.PUT_CALMA_FAIL:
      return {
        ...stateWithLoanSubmitting(state, false),
        isPinValidationLoading: false,
      };

    case actionTypes.PUT_CUSTOMER_MX_NAMES: {
      const {
        firstName,
        lastName,
        secondFirstName,
        secondLastName,
      } = action.data;
      return {
        ...state,
        currentStep: state.currentStep + 1,
        customer: {
          ...state.customer,
          firstName,
          lastName,
          secondFirstName,
          secondLastName,
        },
      };
    }

    case actionTypes.PUT_CUSTOMER_ES_NAMES: {
      const {
        firstName,
        lastName,
        secondLastName,
      } = action.data;
      return {
        ...state,
        currentStep: state.currentStep + 1,
        customer: {
          ...state.customer,
          firstName,
          lastName,
          secondLastName,
        },
      };
    }

    case actionTypes.PUT_CUSTOMER_ES_ADDRESS: {
      const {
        street,
        streetNumber,
        floor,
        door,
        postcode,
        city,
        province,
        msisdn2,
      } = action.data;
      return {
        ...state,
        currentStep: state.currentStep + 1,
        customer: {
          ...state.customer,
          msisdn2,
          address: {
            street,
            streetNumber,
            floor,
            door,
            postcode,
            city,
            province,
          },
        },
        financialData: {
          ...state.financialData,
          residence: action.data.residence,
        },
      };
    }

    case actionTypes.PUT_CUSTOMER_BIRTH_DETAILS: {
      const {
        birthDay,
        birthMonth,
        birthYear,
        countryOfBirth,
        gender,
      } = action.data;
      const dateOfBirth = [birthYear, birthMonth, birthDay];
      return {
        ...state,
        currentStep: state.currentStep + 1,
        customer: {
          ...state.customer,
          gender,
          countryOfBirth,
          dateOfBirth,
        },
      };
    }

    case actionTypes.GET_MX_ADDRESS_SUCCESS:
    case actionTypes.SET_MX_ADDRESS: {
      let data;
      if (action.isPromise) {
        data = action.result.data;
      } else {
        data = action.data;
      }
      return {
        ...state,
        mxAddresses: [].concat(data),
      };
    }

    case actionTypes.PUT_INFO_CREDITEA:
      return putInfoCrediteaState(state, action);

    case actionTypes.PUT_INFO_CREDITEA_SUCCESS: {
      const nextState = stateForNextStepWithLoanSubmitting(state, false);
      if (COUNTRY === 'ES') {
        nextState.customer = merge(state.customer, { email: action.data.email });
        if (state.isFilledByBroker && !state.scoringId) {
          nextState.currentStep -= 1;
          nextState.canPutFinancialData = true;
        }
      }

      if (COUNTRY === 'MX') {
        if (action.result && action.result.config.isMXIncomePage) {
          nextState.canPutFinancialData = true;
          nextState.currentStep -= 1;
        } else if (
          state.currentStep === state.steps.findIndex(({ type }) => type === 'CUSTOMER_MX_CONTACTS')
          && (state.isFilledByBroker || state.isFilledByBrokerPayLater)
          && state.isApplicationWithProduct
        ) {
          nextState.currentStep = state.steps.findIndex(({ type }) => type === 'SUBMIT');
        }
      }

      return nextState;
    }

    case actionTypes.PUT_CONSENTS_SUCCESS: {
      const nextState = stateWithLoanSubmitting(state, false);

      return nextState;
    }

    case actionTypes.CHECK_EXTERNAL_POLICIES_FAIL:
    case actionTypes.FETCH_EXTERNAL_REGISTRIES_FAIL:
    case actionTypes.PUT_INFO_CREDITEA_FAIL:
    case actionTypes.PUT_CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION_FAIL: {
      const error = action.error.data || {};
      const rejectionObj = stateAfterRejection(error);

      if (COUNTRY === 'MX') {
        const { errCode } = error;
        if (errCode === 1451) {
          return {
            ...stateWithLoanSubmitting(state, false),
            emailInUse: action.data.email,
          };
        }
      }

      if (COUNTRY === 'ES') {
        if (error.errCode === 1267) {
          return stateWithLoanSubmitting(state, false);
        }
      }

      return {
        ...state,
        ...rejectionObj,
      };
    }

    case actionTypes.PUT_ADDRESS_SUCCESS:
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        customer: {
          ...state.customer,
          address: action.data.address,
        },
      };

    case actionTypes.FAMILY_AU: {
      const { gender, creditApplication } = action.data;
      return {
        ...stateWithLoanSubmitting(state, true),
        customer: {
          ...state.customer,
          gender,
          creditApplication: {
            ...state.customer.creditApplication,
            ...creditApplication,
          },
        },
      };
    }

    case actionTypes.PUT_LOAN_PURPOSE_SUCCESS: {
      if (state.isFilledByBroker) {
        return {
          ...state,
          currentStep: state.rescoringToDoForOpenCreditApplication
            ? state.currentStep
            : state.currentStep + 1,
        };
      }
      return {
        ...state,
        canPutFinancialData: true,
      };
    }

    case actionTypes.VERIFY_LOAN_PURPOSES_SUCCESS:
      return {
        ...stateForNextStepWithLoanSubmitting(state, false),
        selectedLoanPurposes: action.data.loanPurposes,
        loanPurposeVerificationStatus: action.result.data.status,
      };

    case actionTypes.PUT_BANK_ACCOUNT_SUCCESS: {
      const { bankAccount, bankAccountDuration } = action.data;
      const nextState = {
        ...stateWithLoanSubmitting(state, false),
        customer: {
          ...state.customer,
          bankAccount,
        },
      };
      if (COUNTRY === 'PL') {
        nextState.financialData.bankAccountDuration = bankAccountDuration;
        nextState.canPutFinancialData = true;
      } else if (COUNTRY !== 'EE') {
        nextState.currentStep += 1;
      }
      return nextState;
    }

    case actionTypes.PATCH_GUARANTOR_THEN_FEES_ES_SUCCESS: {
      return state.isApplicationWithProduct
        ? stateForNextStepWithLoanSubmitting(state)
        : stateWithLoanSubmitting(state, false);
    }

    case actionTypes.PUT_PRODUCT_SUCCESS: {
      const nextState = stateForNextStepWithLoanSubmitting(state);
      const { selectedProduct } = action.result.config;

      nextState.productType = selectedProduct.productType;
      nextState.selectedProduct = { ...selectedProduct };

      if (selectedProduct.productType === 'CREDIT_LINE') {
        nextState.currentStep = getIndexStepByName(state.steps, STEP_SUBMIT.type);
      }
      delete nextState.selectedProduct.productType;
      return nextState;
    }

    case actionTypes.LOANS: {
      if (COUNTRY === 'FI') {
        const { loans } = action.data;
        return {
          ...state,
          currentStep: state.currentStep + 1,
          financialData: {
            ...state.financialData,
            loans,
          },
          canPutFinancialData: false,
        };
      }

      if (COUNTRY === 'MX') {
        const creditBureauQuestion1Answer = action.data.creditBureauQuestion1Answer === 'yes';
        const loans = [];
        if (action.data.HOME_LOAN === 'yes') {
          loans.push('HOME_LOAN');
        }
        if (action.data.CREDIT_CARD === 'yes') {
          loans.push('CREDIT_CARD');
        }
        return {
          ...state,
          currentStep: state.currentStep + 1,
          financialData: {
            ...state.financialData,
            creditBureauQuestion1Answer,
            creditBureauQuestion4Answer: action.data.creditBureauQuestion4Answer,
            loans,
          },
          canPutFinancialData: false,
        };
      }

      const { otherDebts, debtTypesExperienced } = action.data;
      return {
        ...state,
        currentStep: state.currentStep + 1,
        financialData: {
          ...state.financialData,
          otherDebts,
          debtTypesExperienced,
        },
        canPutFinancialData: false,
      };
    }

    case actionTypes.PUT_HYBRID_PRODUCT_SUCCESS: {
      const nextState = stateForNextStepWithLoanSubmitting(state);
      const { medicare, billProtector } = state;
      const medicareSelected = medicare && medicare.selected;
      const billProtectorSelected = billProtector && billProtector.selected;
      if (!medicareSelected && !billProtectorSelected) {
        remove(nextState.steps, (item) => item.type === 'VAS_CONFIRM');
      }
      const selectedProduct = action.result.data.selectedProduct;
      const index = findIndex(
        state.availableProducts,
        ({ id }) => parseInt(id, 10) === selectedProduct.id
      );
      nextState.selectedProduct = {
        ...state.availableProducts[index],
        ...selectedProduct,
        ...action.data,
      };
      return nextState;
    }

    case actionTypes.LOANS_AU:
      return {
        ...stateWithLoanSubmitting(state, true),
        customer: {
          ...state.customer,
          creditApplication: {
            ...state.customer.creditApplication,
            ...action.data,
          },
        },
      };

    // for Latvia
    case actionTypes.LOAN_EXPENSES: {
      const { loans, totalMonthlyObligations } = action.data;
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          loans,
          totalMonthlyObligations: parseInt(removeNonDigitCharacters(totalMonthlyObligations), 10),
        },
        canPutFinancialData: false,
      };
    }

    case actionTypes.LOAN_TYPE_LT_SUCCESS: {
      const { loanType, spouseIdentifier, spouseMsisdn } = action.data;
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          loanType,
          spouseIdentifier,
          spouseMsisdn,
        },
      };
    }
    case actionTypes.INCOME_LT_SUCCESS: {
      const {
        employmentDuration, occupation, spouseNetIncome, netIncome,
      } = action.data;
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          employmentDuration,
          occupation,
          netIncome: toCents(netIncome),
          spouseNetIncome: toCents(spouseNetIncome),
        },
      };
    }
    case actionTypes.CLEAR_LOAN_VALIDATION_ERROR: {
      return {
        ...state,
        // TODO:
        errorMessage: null,
      };
    }

    case actionTypes.INCOME: {
      const nextState = {
        ...stateForNextStepWithLoanSubmitting(state),
        canPutFinancialData: false,
      };
      const actionData = action.data;
      if (COUNTRY === 'FI') {
        let steps = [].concat(initialState.steps);
        const { otherLoans } = actionData;
        if (!otherLoans || parseInt(otherLoans, 10) <= 0) {
          // remove LOANS_FI from current step's list
          steps = steps.filter((step) => step.type !== 'LOANS_FI');
        }
        if (VARIANT_FI_ZERO_DRAW) {
          // remove BANK_ACCOUNT, HOUSEHOLD_CHILDREN, PAYMENT_DAY from current step's list
          const stepsToOmit = ['BANK_ACCOUNT', 'HOUSEHOLD_CHILDREN', 'PAYMENT_DAY'];
          steps = steps.filter(({ type }) => !stepsToOmit.includes(type));
        }
        if (EMBEDDED) {
          if (nextState.financialData && nextState.financialData.channel === 'partner_creditea_loan_app') {
            // remove BANK_ACCOUNT from current step's list
            steps = steps.filter((step) => step.type !== 'BANK_ACCOUNT');
          }
        }
        nextState.steps = steps;
        nextState.financialData = {
          ...nextState.financialData,
          salary: toCents(removeNonDigitCharacters(actionData.salary)) || 0,
          benefits: toCents(removeNonDigitCharacters(actionData.benefits)) || 0,
          pension: toCents(removeNonDigitCharacters(actionData.pension)) || 0,
          otherIncome: toCents(removeNonDigitCharacters(actionData.otherIncome)) || 0,
          housingCosts: toCents(removeNonDigitCharacters(actionData.housingCosts)),
          livingCosts: toCents(removeNonDigitCharacters(actionData.livingCosts)),
          otherLoans: toCents(removeNonDigitCharacters(actionData.otherLoans)),
        };
      } else {
        nextState.financialData.netIncome = toCents(removeNonDigitCharacters(actionData.netIncome));
        if (COUNTRY === 'ES') {
          // eslint-disable-next-line max-len
          nextState.financialData.netIncome = toCents(removeNonDigitCharacters(actionData.netIncome));
          // eslint-disable-next-line max-len
          nextState.financialData.livingCosts = toCents(removeNonDigitCharacters(actionData.livingCosts));
        }
        if (COUNTRY === 'LT') {
          nextState.financialData.netIncome = toCents(actionData.netIncome);
          nextState.financialData.occupation = actionData.occupation;
          nextState.financialData.employmentDuration = actionData.employmentDuration;
        }
      }
      return nextState;
    }
    case actionTypes.PL_INCOME: {
      const { netIncome, loanPurpose } = action.data;
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          netIncome: toCents(removeNonDigitCharacters(netIncome)),
          loanPurpose,
        },
        canPutFinancialData: false,
      };
    }
    case actionTypes.EXPENSES:
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          livingCosts: toCents(removeNonDigitCharacters(action.data.livingExpenses)),
        },
        canPutFinancialData: false,
      };

    case actionTypes.MARITAL_STATUS:
      return {
        ...state,
        currentStep: state.currentStep + 1,
        financialData: merge(state.financialData, { maritalStatus: action.data.maritalStatus }),
        canPutFinancialData: false,
      };

    case actionTypes.EDUCATION:

      return {
        ...state,
        currentStep: state.currentStep + 1,
        financialData: {
          ...state.financialData,
          education: action.data.education,
        },
        canPutFinancialData: false,
      };

    case actionTypes.EDUCATION_LT_SUCCESS:

      return {
        ...state,
        currentStep: state.currentStep + 1,
        financialData: {
          ...state.financialData,
          education: action.data.education,
        },
        canPutFinancialData: false,
      };

    case actionTypes.NATIONALITY_LT_SUCCESS:
      if (action.result.data.state === 'REJECTED') {
        return {
          ...state,
          isLoanSubmitting: false,
          state: action.result.data.state,
          rejected: action.result.data.rejectionReason,
          rejectedMessage: action.result.data.rejectionReason,
          canPutFinancialData: false,
        };
      }
      return {
        ...state,
        currentStep: state.currentStep + 1,
        canPutFinancialData: false,
      };

    case actionTypes.WORK_STATUS: {
      return {
        ...state,
        currentStep: state.currentStep + 1,
        financialData: merge({}, state.financialData, action.data.workDetails),
        canPutFinancialData: false,
      };
    }

    case actionTypes.PUT_EDUCATION_WORK_MX_SUCCESS:
    case actionTypes.EDUCATION_WORK_CREDITEA: {
      const {
        education,
        occupation,
        company,
        activityType,
        industry,
        employmentDuration,
        employmentProvisionTermination,
        retirementPensionedBy,
        studiesAtInstitution,
        studiesAtOtherInstitution,
        freelancersIndustry,
      } = action.data;
      const financialData = {
        education,
        company,
        occupation,
        employmentDuration,
      };
      if (COUNTRY === 'ES') {
        const occupationType = workingStateTypes.filter((item) => item.type === occupation)[0];
        if (occupationType.fixedActivityTypeValue) {
          financialData.occupationType = occupationType.fixedActivityTypeValue;
        } else {
          financialData.occupationType = activityType;
        }
        if (occupationType.fixedIndustryValue) {
          financialData.industry = occupationType.fixedIndustryValue;
        } else {
          financialData.industry = industry;
        }
      } else if (COUNTRY === 'PL') {
        financialData.employmentProvisionTermination = employmentProvisionTermination;
        delete financialData.education;
      } else if (COUNTRY === 'MX') {
        financialData.activityType = activityType;
        financialData.retirementPensionedBy = retirementPensionedBy;
        financialData.studiesAtInstitution = studiesAtInstitution;
        financialData.studiesAtOtherInstitution = studiesAtOtherInstitution;
        financialData.freelancersIndustry = freelancersIndustry;
      }
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          ...financialData,
        },
        canPutFinancialData: false,
      };
    }

    case actionTypes.FAMILY_CREDITEA:
    case actionTypes.PUT_FAMILY_CREDITEA_SUCCESS:
    case actionTypes.PUT_FAMILY_MX_SUCCESS: {
      const { data: { maritalStatus, loanPurpose }, result: { data: { scoringId } } } = action;
      const stateForNextStep = { ...state };
      if (COUNTRY === 'ES' && state.rescoringToDoForOpenCreditApplication) {
        stateForNextStep.currentStep = state.currentStep - 1;
      }
      const nextState = {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          maritalStatus,
        },
        canPutFinancialData: false,
        scoringId: scoringId || state.scoringId,
      };
      if (loanPurpose) {
        nextState.financialData.loanPurpose = loanPurpose;
      }
      return nextState;
    }

    case actionTypes.RESIDENCE: {
      const nextState = {
        ...stateForNextStepWithLoanSubmitting(state),
        financialData: {
          ...state.financialData,
          residence: action.data.residence,
        },
      };

      let canPutFinancialData = false;
      if (MOBILE_WALLET || VARIANT_FI_ZERO_DRAW) {
        canPutFinancialData = true;
      }

      if (COUNTRY === 'EE') {
        canPutFinancialData = findIndex(state.steps, (item) => item.type === 'POST_OFFICE') === -1;
      }

      if (canPutFinancialData) {
        nextState.currentStep = state.currentStep;
        nextState.canPutFinancialData = true;
      }

      return nextState;
    }
    case actionTypes.PUT_REFERENCE_SUCCESS: {
      if (state.currentStep === state.steps.length - 1) {
        return {
          ...stateWithLoanSubmitting(state, false),
          isLoanSubmitted: true,
        };
      }
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        customer: {
          ...state.customer,
          reference: action.data,
        },
      };
    }

    case actionTypes.VERIFICATION_STATE_RESTORE: {
      const nextState = {
        ...state,
      };

      const rootState = store.getState();

      const { data } = action;
      const {
        eidSucceeded,
        kontomatikSuccess,
        bankStatementSuccess,
        bankStatementSkipped,
        calmaPassed,
        pinSuccess,
        dueDateSet,
        libertoSkipped,
        libertoSuccess,
      } = data;

      if (state.state !== 'OPEN') {
        if (COUNTRY === 'MX') {
          nextState.bankStatementSuccess = bankStatementSuccess;
          nextState.bankStatementSkipped = bankStatementSkipped;
        }
        if (COUNTRY === 'ES') {
          nextState.eidSucceeded = eidSucceeded;
          nextState.kontomatikSuccess = kontomatikSuccess;
          nextState.calmaPassed = calmaPassed;
          nextState.pinSuccess = pinSuccess;
          nextState.dueDateSet = dueDateSet;
          nextState.bankStatementSuccess = bankStatementSuccess;
          nextState.bankStatementSkipped = bankStatementSkipped;
          nextState.isLoanSubmitted = rootState.authentication.authenticationMethod === 'IMPERSONATION' || (
            dueDateSet
            && calmaPassed
            && (PARTNER_PORTAL || (libertoSkipped || libertoSuccess))
            && (
              PARTNER_PORTAL
              || EMBEDDED
              || (
                eidSucceeded
                && kontomatikSuccess
                && (bankStatementSuccess || bankStatementSkipped)
              )
            )
          );
        }
        if (COUNTRY === 'LT') {
          nextState.isLoanSubmitted = data.finpassSuccess;
          nextState.isFinpassStarted = data.finpassStarted;
        }
        if (COUNTRY === 'CZ') {
          const {
            incomeVerificationWay,
            identityVerificationWay,
            identityDocumentsUploadSuccess,
          } = data;
          nextState.incomeVerificationWay = incomeVerificationWay;
          nextState.identityVerificationWay = identityVerificationWay;
          nextState.isLoanSubmitted = (
            (identityVerificationWay === 'EMAIL' || identityDocumentsUploadSuccess)
            && (incomeVerificationWay === 'EMAIL' || kontomatikSuccess || bankStatementSuccess)
          );
        }
      }

      return nextState;
    }

    case actionTypes.SCORING_STATUS_ES_SUCCESS: {
      const { currentStep, reject } = action.result.data;
      const { authentication: { isApplyGuarantor, mainApplicationId } } = store.getState();

      const {
        steps,
        statusMaxTimeout,
        waitingForOfferInterval,
        statusCheckAttepts,
        rescoringToDoForOpenCreditApplication,
      } = state;
      let nextState = {
        ...state,
        isContentLoading: false,
        technicalError: false,
        statusCheckAttepts: statusCheckAttepts + 1,
      };
      const isFinished = currentStep === 'FINISHED';
      const isWaitingForCustomer = currentStep === 'WAITING_FOR_CUSTOMER'
        || currentStep === 'WAITING_FOR_CUSTOMER_AND_NORDIGEN_BEFORE_OFFER';
      const isRejected = currentStep === 'REJECTED';
      if (isRejected && action.result.state === 410) {
        const newSteps = [...steps];
        const isSecondScoring = state.lastStatusPage === 'status_2';
        const fistScoringStepIndex = steps.findIndex((step) => step.type === 'SCORING_STATUS');
        const scoringStep = steps[fistScoringStepIndex];
        nextState.rescoringToDoForOpenCreditApplication = 1;
        const kontomatikStepIndex = newSteps.findIndex((step) => step.type === 'KONTOMATIK');
        const kontomatikStep = newSteps[kontomatikStepIndex];
        pullAt(newSteps, kontomatikStepIndex);
        if (isSecondScoring) {
          nextState.rescoringToDoForOpenCreditApplication = 2;
          pullAt(newSteps, fistScoringStepIndex);
          const secondScoringStepIndex = newSteps.findIndex((step) => step.type === 'SCORING_STATUS');
          newSteps.splice(secondScoringStepIndex + 1, 0, kontomatikStep);
          newSteps.splice(secondScoringStepIndex + 2, 0, scoringStep);
        }
        newSteps.splice(fistScoringStepIndex + 1, 0, kontomatikStep);
        nextState.steps = newSteps;
      } else if (isRejected) {
        nextState = {
          ...nextState,
          ...stateAfterRejection(reject),
          finalState: true,
          statusCheckAttepts: 0,
        };
      } else if ((isFinished && rescoringToDoForOpenCreditApplication) || isWaitingForCustomer) {
        nextState.currentStep += 1;
        nextState.statusCheckAttepts = 0;
        nextState.finalState = true;
      } else if (isFinished) {
        nextState.finalState = true;

        if (isApplyGuarantor && mainApplicationId) {
          nextState.rejected = 'default';
        } else {
          nextState.currentStep = steps.findIndex(({ type }) => type === 'GUARANTOR');
        }
        nextState.statusCheckAttepts = 0;
      } else if (currentStep === 'FAILED') {
        nextState.finalState = true;
        nextState.statusCheckAttepts = 0;
        nextState.technicalError = true;
      }

      if ((statusMaxTimeout / waitingForOfferInterval) < statusCheckAttepts) {
        nextState.finalState = true;
        nextState.technicalError = true;
      }
      return nextState;
    }

    case actionTypes.SCORING_STATUS_AU_SUCCESS: {
      const data = action.result.data;
      const nextState = stateWithContentLoading(state, false);
      if (data.finalState) {
        const { applicationState } = data;
        if (applicationState === 'DECISION_ENGINE_SUCCESS') {
          nextState.currentStep += 1;
          nextState.isScoringStatusReceived = true;
        } else {
          const rejectionObj = stateAfterRejection(applicationState);
          rejectionObj.rejectedMessage = applicationState;
          return {
            ...state,
            ...rejectionObj,
          };
        }
      } else if (state.waitingForOfferCounter < state.waitingForOfferCounterMax) {
        nextState.waitingForOfferCounter += 1;
      } else if (state.waitingForOfferCounter >= state.waitingForOfferCounterMax) {
        nextState.isWaitingForOffer = false;
        nextState.bankScrapingTimedOut = true;
        nextState.waitingForOfferCounter = 0;
      }

      return nextState;
    }

    case actionTypes.HOUSEHOLD_CHILDREN: {
      const nextState = {
        ...state,
        financialData: {
          ...state.financialData,
          householdChildren: action.data.householdChildren,
        },
      };
      if (COUNTRY === 'EE') {
        nextState.currentStep += 1;
      } else {
        nextState.canPutFinancialData = true;
      }
      return nextState;
    }

    case actionTypes.OBLIGATIONS: {
      const {
        netIncome,
        monthlyPersonalExpenses,
        otherMonthlyObligations,
        numberOfDependents,
      } = action.data;
      return {
        ...state,
        financialData: {
          ...state.financialData,
          netIncome: toCents(netIncome) || 0,
          monthlyPersonalExpenses: toCents(monthlyPersonalExpenses) || 0,
          otherMonthlyObligations: toCents(otherMonthlyObligations) || 0,
          numberOfDependents,
        },
        canPutFinancialData: true,
      };
    }

    case actionTypes.LOAN_QUESTIONS: {
      const {
        data: {
          loanPurpose,
          learnedAboutUsFrom,
        },
      } = action;

      const nextState = {
        ...state,
        financialData: merge(
          state.financialData,
          {
            loanPurpose,
            learnedAboutUsFrom,
          }
        ),
        canPutFinancialData: true,
      };
      return nextState;
    }

    case actionTypes.PROVIDENT_SCORING_MX: {
      return {
        ...state,
        providentScoring: action.data,
        // Display loading page
        isWaitingForOffer: true,
        isLoanFormFooterVisible: false,
      };
    }

    case actionTypes.PROVIDENT_SCORING_MX_SUCCESS: {
      const { financialRejectReason: message, currentStep, steps } = state;
      const {
        riskGrade,
        noHitProductForRiskGrades,
        showPsychometricTest,
        score,
      } = action.result.data;

      let nextState = {
        providentScoring: action.result.data,
        // Hide loading page
        isWaitingForOffer: false,
        isLoanFormFooterVisible: false,
      };

      const isRiskGradeInRange1To5 = riskGrade >= 1 && riskGrade <= 5;
      const {
        stepName,
        isOffer,
        isProvidentProduct,
      } = getStepAfterProvidentScoring(
        riskGrade,
        score,
        showPsychometricTest,
        noHitProductForRiskGrades,
        message
      );

      if (isRiskGradeInRange1To5) {
        if (isOffer) {
          nextState.currentStep = getIndexStepByName(steps, stepName);
          nextState.isLoanFormFooterVisible = true;
          nextState.isLoanSubmitting = false;
          nextState.isWaitingForOffer = false;
          nextState.isContentLoading = false;
        } else {
          const nextStep = isProvidentProduct ? STEP_PROVIDENT_PRODUCT : STEP_PSYCHOMETRIC;
          nextState.steps = addStepToLoanForm(currentStep, steps, nextStep);
          nextState.currentStep = getIndexStepByName(nextState.steps, nextStep);
        }
      } else {
        nextState = {
          ...nextState,
          providentScoring: null,
          isLoanSubmitting: false,
          finalState: true,
          rejected: 'default',
          rejectedMessage: 'Provi_Score_cut',
        };
      }

      return {
        ...state,
        ...nextState,
      };
    }

    case actionTypes.PROVIDENT_SCORING_MX_FAIL: {
      return {
        ...state,
        providentScoring: null,
        isLoanSubmitting: false,
        finalState: true,
        isWaitingForOffer: false,
        isLoanFormFooterVisible: true,
        rejected: 'default',
        rejectedMessage: 'ProvidentScoringFailed',
      };
    }

    case actionTypes.PROVIDENT_UPDATE: {
      return {
        ...state,
      };
    }

    case actionTypes.PUT_FINANCIAL: {
      const nextState = {
        ...stateWithLoanSubmitting(state, true),
        rejected: null,
        rejectedMessage: null,
        financialData: { ...action.data },
      };
      if (COUNTRY === 'PL' || COUNTRY === 'ES') {
        nextState.isWaitingForOffer = true;
        nextState.isLoanFormFooterVisible = false;
      }
      return nextState;
    }

    case actionTypes.PUT_FINANCIAL_SUCCESS: {
      const currentStep = state.rescoringToDoForOpenCreditApplication
        ? state.currentStep
        : state.currentStep + 1;
      const financialData = { ...action.data };
      const nextState = {
        ...stateWithLoanSubmitting(state, false),
        currentStep,
        canPutFinancialData: false,
        financialData,
      };
      if (COUNTRY === 'PL' || COUNTRY === 'ES') {
        nextState.isWaitingForOffer = false;
        nextState.isLoanFormFooterVisible = true;
      }
      if (COUNTRY === 'ES') {
        nextState.scoringId = action.result.data.id;
        if (state.isFilledByBroker) {
          nextState.currentStep = state.currentStep;
        }
      }
      if (COUNTRY === 'MX') {
        nextState.isFilledByBroker = false;
      }
      return nextState;
    }

    case actionTypes.GET_OFFER_PRODUCTS_AU_FAIL:
      return {
        ...state,
        technicalError: true,
      };

    case actionTypes.GET_MIGRATION_PRODUCTS_FAIL:
    case actionTypes.GET_PRODUCTS_FAIL:
    case actionTypes.GET_PRODUCTS_FOR_CREDIT_LIMIT_FAIL:
    case actionTypes.PUT_FINANCIAL_FAIL: {
      const { message } = action.error.data;
      const isRejectionToProvidentScoring = message === CREDIT_REJECTION_EXT_SCORE_CUT
        || message === CREDIT_REJECTION_BDC_SCORING_NEGATIVE;

      if (COUNTRY === 'MX' && isRejectionToProvidentScoring) {
        return { ...state, financialRejectReason: message };
      }

      const error = action.error;
      const errorData = error.data || {};
      const rejectionObj = stateAfterRejection(errorData);
      const nextState = {
        ...state,
        ...rejectionObj,
        canPutFinancialData: false,
        isWaitingForOffer: false,
      };
      if (COUNTRY === 'PL' || COUNTRY === 'ES') {
        nextState.isLoanFormFooterVisible = true;
      }
      if (COUNTRY === 'MX') {
        nextState.isFilledByBroker = false;
      }
      if (COUNTRY === 'LV') {
        nextState.technicalError = error.status !== 412;
      }
      return { ...nextState, financialRejectReason: message };
    }

    case actionTypes.GET_DATE_RANGE:
    case actionTypes.GET_LOAN_ISSUERS:
    case actionTypes.GET_COUNTRY_LIST:
    case actionTypes.GET_APPLICABLE_CAMPAIGNS:
    case actionTypes.GET_POST_OFFICES:
    case actionTypes.GET_CITIZENSHIPS:
    case actionTypes.GET_REGISTRATION_STATUS:
    case actionTypes.POST_EID_AUTHORIZATION_TOKEN:
    case actionTypes.REPAYMENT_PLAN_AVAILABILITY:
    case actionTypes.CALMA_AVAILABILITY:
    case actionTypes.GET_OFFER_PRODUCTS_AU:
    case actionTypes.GET_LEGAL_DOCUMENT:
    case actionTypes.GET_LOAN_PURPOSES:
    case actionTypes.PUT_KONTOMATIK_SESSION:
    case actionTypes.POST_BANK_STATEMENTS:
    case actionTypes.POST_IDENTITY_DOCUMENTS:
    case actionTypes.GET_CHANNEL_LIST:
    case actionTypes.GET_BANK_ACCOUNT:
    case actionTypes.GET_FINPASS_BANKS:
    case actionTypes.GET_PROVISO_IFRAME_URL:
    case actionTypes.GET_FEES:
    case actionTypes.GET_CONTRACT:
    case actionTypes.GET_NORDIGEN_BANKS:
    case actionTypes.GET_NORDIGEN_ACCOUNT:
      return {
        ...stateWithContentLoading(state, true),
      };
    case actionTypes.GET_MMP_BY_DUE_DATE:
      return {
        ...stateWithContentLoading(state, COUNTRY !== 'LT'),
      };

    case actionTypes.SCORING_STATUS_AU:
    case actionTypes.SCORING_STATUS_ES:
    case actionTypes.POST_MIGRATION_SCORING_ES:
    case actionTypes.GET_MIGRATION_SCORING_ES:
      return {
        ...stateWithContentLoading(state, true),
        isWaitingForOffer: true,
      };

    case actionTypes.PUT_KONTOMATIK_SESSION_FAIL:
      return {
        ...stateWithContentLoading(state, false),
        currentStep: state.currentStep + 1,
      };

    case actionTypes.PUT_KONTOMATIK_SESSION_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        currentStep: state.currentStep + 1,
        kontomatikSuccess: true,
      };

    case actionTypes.SKIP_EID:
      return {
        ...state,
        eidSucceeded: true,
      };

    case actionTypes.SKIP_KONTOMATIK:
      return {
        ...state,
        kontomatikSuccess: true,
      };

    case actionTypes.SKIP_BANK_STATEMENTS:
      return {
        ...state,
        bankStatementSkipped: true,
        isLoanSubmitted: true,
      };

    case actionTypes.POST_BANK_STATEMENTS_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        bankStatementSuccess: true,
        isLoanSubmitted: COUNTRY === 'ES' || COUNTRY === 'MX',
        currentStep: COUNTRY === 'CZ' ? state.currentStep + 1 : state.currentStep,
      };

    case actionTypes.POST_IDENTITY_DOCUMENTS_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        isLoanSubmitted: true,
      };

    case actionTypes.GET_PAYMENT_PLAN:
      return {
        ...stateWithContentLoading(state, COUNTRY !== 'ES' && COUNTRY !== 'PL'),
      };

    case actionTypes.GET_DATE_RANGE_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        loanDueDateRange: action.result.data,
      };

    case actionTypes.GET_MMP_BY_DUE_DATE_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        loanMmp: action.result.data.mmp,
        loanTotalAmount: action.result.data.totalAmount,
      };

    case actionTypes.GET_LOAN_ISSUERS_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        loanIssuers: action.result.data,
      };

    case actionTypes.GET_PAYMENT_PLAN_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        paymentPlan: action.result.data.paymentPlan,
      };

    case actionTypes.POST_EID_AUTHORIZATION_TOKEN_SUCCESS: {
      return {
        ...stateWithContentLoading(state, false),
        eidAuthorizationToken: action.result.data.authorization,
      };
    }

    case actionTypes.POST_ONFIDO_AUTHORIZATION_SUCCESS: {
      const { token, applicantId } = action.payload;
      return {
        ...stateWithContentLoading(state, false),
        onfidoApplicant: { id: applicantId, token },
      };
    }

    case actionTypes.POST_ONFIDO_CHECK_SUCCESS: {
      return {
        ...stateWithContentLoading(state, false),
        onfidoSucceeded: true,
      };
    }
    case actionTypes.POST_ONFIDO_SKIPPED: {
      return {
        ...stateWithContentLoading(state, false),
        onfidoSucceeded: false,
      };
    }

    case actionTypes.POST_ONFIDO_AUTHORIZATION_FAIL:
    case actionTypes.POST_ONFIDO_CHECK_FAIL: {
      const { errorMessage } = action.payload;
      return {
        ...stateWithContentLoading(state, false),
        onfidoError: { errorMessage },
      };
    }

    case actionTypes.SAVE_EID_RESULT_SUCCESS: {
      const { result: { config } } = action;
      if (config.finishedDueToHarshError) {
        return {
          ...state,
          ...stateAfterRejection({
            message: 'fraud',
          }),
        };
      }

      const data = JSON.parse(config.data);
      return {
        ...state,
        eidSucceeded: data.status === 'COMPLETED',
      };
    }

    case actionTypes.GET_COUNTRY_LIST_SUCCESS: {
      const countryList = action.result.data;
      let orderedCountries;
      if (COUNTRY === 'ES') {
        const spain = countryList.filter((country) =>
          country.code === 'ES');
        orderedCountries = spain.concat(
          countryList.filter((country) =>
            country.code !== 'ES').sort((a, b) =>
            a.name.localeCompare(b.name))
        );
      } else {
        orderedCountries = countryList.sort((a, b) =>
          a.name.localeCompare(b.name));
      }
      let countries;
      if (COUNTRY === 'LT') {
        countries = orderedCountries.map((item) =>
          ({ type: item.code, title: `country.${item.code}`, intl: true }));
      } else {
        countries = orderedCountries.map((item) =>
          ({ type: item.code, title: item.name }));
      }

      return {
        ...stateWithContentLoading(state, false),
        countries,
      };
    }

    case actionTypes.GET_ADDRESS_SUGGESTIONS_SUCCESS: {
      const { type, autofill } = action.data;
      let suggestionsData = action.result.data;
      if (autofill) {
        if (suggestionsData.length === 1) {
          return { ...state, autocompletedField: type };
        }
        if (suggestionsData.length) {
          return state;
        }
      }
      if (COUNTRY === 'CZ') {
        suggestionsData = suggestionsData.reduce(
          (result, suggestion) => {
            // Backend task: sanitize addresses from non-printable characters
            const house = suggestion.house.replace(/\r/, '');

            return {
              ...result,
              [`${suggestion.street} ${house}, ${suggestion.city}, ${suggestion.postcode}`]: {
                ...suggestion,
                house,
              },
            };
          },
          {}
        );
      }
      return {
        ...state,
        addressSuggestions: {
          name: type,
          data: suggestionsData,
        },
      };
    }

    case actionTypes.RESET_ADDRESS_SUGGESTIONS: {
      return {
        ...state,
        addressSuggestions: {
          name: '',
          data: [],
        },
      };
    }

    case actionTypes.RESET_ROBIN_REDIRECT_ID: {
      return {
        ...state,
        robinRedirectId: null,
      };
    }

    case actionTypes.GET_APPLICABLE_CAMPAIGNS_SUCCESS: {
      const items = EMBEDDED && state.financialData.channel === 'partner_rahapalvelu_loan_app'
        ? []
        : action.result.data.filter((location) =>
          moment().isBetween(location.start, location.validUntil)).map((item) => {
          const duration = moment(item.end).diff(moment(item.start), 'days');
          return {
            ...item,
            duration,
            monthlyDuration: duration / 30,
          };
        });
      return {
        ...stateWithContentLoading(state, false),
        applicableCampaigns: items,
      };
    }

    case actionTypes.GET_POST_OFFICES_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        postOffices: sortBy(action.result.data, 'name').map((office) => (
          { type: office.ID, title: `${office.name}, ${office.address}`, intl: false }
        )),
      };

    case actionTypes.GET_CITIZENSHIPS_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        citizenships: sortBy(action.result.data, 'label').map((country) => (
          { type: country.alpha3, title: country.label, intl: false }
        )),
      };

    case actionTypes.GET_DATE_RANGE_FAIL:
    case actionTypes.GET_LOAN_ISSUERS_FAIL:
    case actionTypes.GET_PAYMENT_PLAN_FAIL:
    case actionTypes.GET_COUNTRY_LIST_FAIL:
    case actionTypes.GET_APPLICABLE_CAMPAIGNS_FAIL:
    case actionTypes.GET_POST_OFFICES_FAIL:
    case actionTypes.GET_CITIZENSHIPS_FAIL:
    case actionTypes.GET_REGISTRATION_STATUS_FAIL:
    case actionTypes.POST_EID_AUTHORIZATION_TOKEN_FAIL:
    case actionTypes.REPAYMENT_PLAN_AVAILABILITY_FAIL:
    case actionTypes.CALMA_AVAILABILITY_FAIL:
    case actionTypes.GET_LEGAL_DOCUMENT_FAIL:
    case actionTypes.GET_LOAN_PURPOSES_FAIL:
    case actionTypes.SCORING_STATUS_AU_FAIL:
    case actionTypes.GET_MMP_BY_DUE_DATE_FAIL:
    case actionTypes.PUT_UPDATE_LIVING_ADDRESS_FAIL:
    case actionTypes.NATIONALITY_LT_FAIL:
    case actionTypes.LIABILITIES_LT_FAIL:
    case actionTypes.GET_CHANNEL_LIST_FAIL:
    case actionTypes.GET_BANK_ACCOUNT_FAIL:
    case actionTypes.GET_FINPASS_BANKS_FAIL:
    case actionTypes.GET_FINPASS_REDIRECT_URL_FAIL:
    case actionTypes.GET_PROVISO_IFRAME_URL_FAIL:
    case actionTypes.POST_BANK_STATEMENTS_FAIL:
    case actionTypes.POST_IDENTITY_DOCUMENTS_FAIL:
    case actionTypes.GET_FEES_FAIL:
    case actionTypes.GET_NORDIGEN_BANKS_FAIL:
    case actionTypes.GET_NORDIGEN_ACCOUNT_FAIL:
    case actionTypes.GET_PRODUCT_NAME_FAIL:
      return {
        ...stateWithContentLoading(state, false),
      };
    case actionTypes.SCORING_STATUS_ES_FAIL:
      return {
        ...stateWithContentLoading(state, false),
        technicalError: true,
        finalState: true,
      };
    case actionTypes.GET_INSTALLMENT_FAIL: {
      const nextState = {
        ...stateWithContentLoading(state, false),
      };
      if (COUNTRY === 'LV') {
        nextState.technicalError = true;
      }
      return nextState;
    }

    case actionTypes.PUT_PAYMENT_DAY: {
      const isLoanSubmitting = !(COUNTRY === 'ES' || COUNTRY === 'PL');
      return {
        ...stateWithLoanSubmitting(state, isLoanSubmitting),
        isPaymentDaySubmitting: true,
      };
    }

    case actionTypes.PUT_PAYMENT_DAY_SUCCESS: {
      let data;

      if (action.isPromise) {
        const result = action.result.filter((item) => item.config.isPaymentDateRequest).shift();
        if (result && result.config.method === 'put') {
          data = JSON.parse(result.config.data);
        } else {
          data = { dueDate: state.loanDueDateRange.defaultDate };
        }
      } else {
        data = action.data;
      }

      const nextState = {
        ...stateForNextStepWithLoanSubmitting(state),
        isPaymentDaySubmitting: false,
        ...data,
      };

      if (
        COUNTRY === 'LT'
        || COUNTRY === 'PL'
        || COUNTRY === 'ES'
        || COUNTRY === 'CZ'
      ) {
        nextState.currentStep -= 1;
      }

      return nextState;
    }

    case actionTypes.PUT_PAYMENT_DAY_FAIL:
      return {
        ...stateWithLoanSubmitting(state, false),
        isPaymentDaySubmitting: false,
      };

    case actionTypes.SET_SELECTED_PRODUCT: {
      return {
        ...state,
        selectedProduct: action.payload.product,
      };
    }

    case actionTypes.GET_PRODUCTS:
    case actionTypes.GET_PRODUCTS_FOR_CREDIT_LIMIT: {
      const nextState = {
        ...stateWithContentLoading(state, true),
        showCalculator: false,
      };

      if (COUNTRY === 'ES') {
        nextState.isWaitingForOffer = true;
        nextState.isLoanFormFooterVisible = false;
      }

      return nextState;
    }

    case actionTypes.GET_PRODUCTS_SUCCESS: {
      const products = sortBy(action.result.data, 'principal').reverse();
      let selectedProduct;
      if (
        (COUNTRY === 'FI' || COUNTRY === 'LV' || COUNTRY === 'ES')
        && (state.isFilledByBroker || (VARIANT_B && state.canChangeLimit))
        && state.selectedProduct
        && state.selectedProduct.id
      ) {
        // use already selected by broker product from credit-application
        selectedProduct = state.selectedProduct;
        if (COUNTRY === 'ES') {
          selectedProduct.needsReplacement = true;
        }
      } else {
        selectedProduct = products[0];
      }

      let isContentLoading = false;
      let availablePrincipalsIndex = [];
      const availableSumsIndex = [];

      if (COUNTRY === 'LV') {
        if (products.length === 0) {
          isContentLoading = true;
        } else if (selectedProduct) {
          // eslint-disable-next-line max-len
          // Since the max principal can be a value that is not divisible by the slider step
          // eslint-disable-next-line max-len
          // it is necessary to use an index-based slider with a step of 1 to avoid a bug, for this,
          // eslint-disable-next-line max-len
          // the actual values of the slider are taken from availablePrincipalsIndex generated below
          // from 0 to maxPrincipal using the step. If the biggest value is not
          // a multiple of the step, it is added manually to the end of the principals
          // for more info, see https://jira.ipfdigital.tech/browse/PXLM-2273
          const maxPrincipal = selectedProduct.principal;
          const indiceCount = parseInt(maxPrincipal / state.product.step, 10);
          for (let i = 0; i < indiceCount; i += 1) {
            availablePrincipalsIndex.push(i * state.product.step);
          }
          if (!availablePrincipalsIndex.includes(maxPrincipal)) {
            availablePrincipalsIndex.push(maxPrincipal);
          }
        }
      }

      if (COUNTRY === 'EE' || COUNTRY === 'CZ' || (COUNTRY === 'LV' && VARIANT_B && state.canChangeLimit)) {
        availablePrincipalsIndex = products.map((product) => product.principal).reverse();
      }

      const nextState = {
        ...stateWithContentLoading(state, isContentLoading),
        selectedProduct,
        product: {
          ...state.product,
          firstDrawAmount: selectedProduct ? selectedProduct.principal : 0,
        },
        availablePrincipalsIndex,
        availableSumsIndex,
      };
      if (COUNTRY === 'CZ') {
        nextState.availableProducts = products;
      }
      if (COUNTRY === 'CZ') {
        const { maturityPeriods } = selectedProduct;
        let periods = [...possiblePeriodsCL];
        if (!periods.includes(maturityPeriods)) {
          periods.push(maturityPeriods);
          periods.sort((a, b) => (a - b));
        }
        const indexOfProductMaturity = periods.indexOf(maturityPeriods);
        if (indexOfProductMaturity < 3) {
          periods = [1, 2, 3];
        } else {
          periods = periods.slice(indexOfProductMaturity - 3, indexOfProductMaturity + 1);
        }
        nextState.selectedProduct.maturitiesToLoad = periods;
      }
      if (COUNTRY === 'ES') {
        nextState.selectedProduct.maturitiesToLoad = [
          ...savingTablePeriods.filter((period) =>
            period <= nextState.selectedProduct.maturityPeriods),
        ];

        if (!nextState.selectedProduct.firstDrawAmount) {
          nextState.selectedProduct.firstDrawAmount = nextState.product.firstDrawAmount;
        }
      }

      if (COUNTRY === 'LT' || COUNTRY === 'EE' || (COUNTRY === 'LV' && VARIANT_B)) {
        nextState.productsByPrincipals = {};
        products.forEach((product) => {
          nextState.productsByPrincipals[product.principal] = product;
        });
      }

      if (COUNTRY === 'ES') {
        nextState.isWaitingForOffer = false;
        nextState.isLoanFormFooterVisible = true;
      }

      return nextState;
    }

    case actionTypes.GET_PRODUCTS_FOR_CREDIT_LIMIT_SUCCESS: {
      const products = sortBy(action.result.data, 'principal').reverse();
      const availablePrincipalsIndex = products.map((product) => product.principal)
        .reverse()
        .filter((elem, index, self) => index === self.indexOf(elem));

      const nextState = {
        ...stateWithContentLoading(state, false),
        availableProducts: products,
        availablePrincipalsIndex,
      };

      return nextState;
    }

    case actionTypes.GET_INSTALLMENT: {
      const nextState = {
        ...stateWithContentLoading(state, true),
        showCalculator: false,
        productType: 'INSTALLMENT',
      };
      if (COUNTRY === 'PL' || COUNTRY === 'ES') {
        nextState.isWaitingForOffer = true;
        nextState.isLoanFormFooterVisible = false;
      }
      return nextState;
    }

    case actionTypes.GET_INSTALLMENT_SUCCESS: {
      let periods = [6, 12, 24, 36];
      if (COUNTRY === 'ES') {
        periods = [1, 3, 6, 12, 18, 24];
      }
      const preSelectedMaturity = state.product.maturityPeriods;
      if (!periods.includes(preSelectedMaturity)) {
        periods = [...periods, preSelectedMaturity];
      }
      let availableProducts = action.result.data.products
        ? action.result.data.products
        : action.result.data;
      if (COUNTRY !== 'PL' && COUNTRY !== 'LT') {
        availableProducts = filter(action.result.data, (product) =>
          periods.includes(product.maturityPeriods));
      }
      let availablePrincipalsIndex = uniqBy(availableProducts, (item) =>
        item.principal);
      availablePrincipalsIndex = sortBy(availablePrincipalsIndex, (item) =>
        item.principal);
      availablePrincipalsIndex = availablePrincipalsIndex.map((availablePrincipal) =>
        availablePrincipal.principal);

      const maxPrincipalAvailable = Math.max(...availablePrincipalsIndex);
      const selectedProductValue = maxPrincipalAvailable;
      let selectedProduct;
      const isSilent = action.result.config.silent;
      if (isSilent) {
        selectedProduct = state.selectedProduct;
      } else {
        selectedProduct = filter(availableProducts, {
          principal: selectedProductValue,
        });
        selectedProduct = maxBy(selectedProduct, (item) =>
          item.maturityPeriods);
      }

      const selectedProductPriceIndex = availablePrincipalsIndex.indexOf(selectedProductValue);
      const nextState = {
        ...stateWithContentLoading(state, false),
        availableProducts,
        availablePrincipalsIndex,
        selectedProductPriceIndex,
        selectedProduct,
        product: {
          ...state.product,
          firstDrawAmount: maxPrincipalAvailable,
        },
      };
      if (COUNTRY === 'PL' || COUNTRY === 'ES') {
        nextState.isWaitingForOffer = false;
        nextState.isLoanFormFooterVisible = true;
      }
      return nextState;
    }

    case actionTypes.GET_LOAN_PURPOSES_SUCCESS: {
      const loanPurposes = action.result.data;
      return {
        ...stateWithContentLoading(state, false),
        loanPurposes,
        loanPurposeSuggestions: loanPurposes.reduce(
          (result, category) => {
            const subs = category.purposes.map((purpose) => ({
              ...purpose,
              fullText: `${category.category} - ${purpose.name}`,
              key: purpose.id,
            }));
            return [
              ...result,
              ...subs,
            ];
          },
          []
        ),
      };
    }

    case actionTypes.VERIFICATION:
    case actionTypes.CHOOSE_PRODUCT:
    case actionTypes.PUT_CUSTOMER_MX_BANK_ACCOUNT_VERIFICATION_SUCCESS: {
      if (COUNTRY === 'MX' && action.type === actionTypes.VERIFICATION) {
        return {
          ...state,
          isLoanSubmitted: true,
        };
      }
      return {
        ...stateWithLoanSubmitting(state, false),
        currentStep: state.currentStep + 1,
      };
    }

    case actionTypes.KONTOMATIK: {
      if (COUNTRY === 'MX' && action.type === actionTypes.VERIFICATION) {
        return {
          ...state,
          isLoanSubmitted: true,
        };
      }

      const nextState = {
        ...stateWithContentLoading(state, false),
      };

      if (COUNTRY === 'ES') {
        nextState.currentStep += 1;
      }

      return nextState;
    }

    case actionTypes.LEGAL: {
      return stateForNextStepWithLoanSubmitting(state, false);
    }

    case actionTypes.CLOSE_POPUP: {
      const nextState = {
        ...stateWithContentLoading(state, false),
        showPopup: false,
      };
      const currentStepType = state.steps.find((_, index) => index === state.currentStep).type;
      if (currentStepType !== 'EID_INTRO' && currentStepType !== 'EID') {
        nextState.popupShowed = true;
      }
      return nextState;
    }

    case actionTypes.EID_INTRO:
      return {
        ...state,
        currentStep: state.currentStep + 1,
      };

    case actionTypes.SET_PRINCIPAL: {
      return {
        ...state,
        product: merge({}, state.product, { principal: toCents(action.data) }),
      };
    }

    case actionTypes.SET_MATURITY_PERIODS:
      return {
        ...state,
        product: {
          ...state.product,
          maturityPeriods: action.data,
        },
      };

    case actionTypes.START_BLUEMEDIA: {
      return {
        ...state,
        isBluemediaLoading: true,
      };
    }

    case actionTypes.START_BLUEMEDIA_SUCCESS: {
      const nextState = {
        ...state,
        isBluemediaLoading: false,
      };
      if (EMBEDDED) {
        const { data: { url, payload } } = action.result;
        const params = queryStringParser.parse(payload);
        nextState.bluemediaParams = { url, params };
      }
      return nextState;
    }

    case actionTypes.START_BLUEMEDIA_FAIL: {
      return {
        ...state,
        isBluemediaLoading: false,
      };
    }

    case actionTypes.GET_MMPS_BY_GROUP: {
      return {
        ...state,
        isLoadingMMP: true,
        isServiceUnavailable: false,
      };
    }

    case actionTypes.GET_MMPS_BY_GROUP_SUCCESS: {
      let mmpsByGroup = flatten(action.result.data, { maxDepth: 2 });
      mmpsByGroup = map(mmpsByGroup, (item) => ({
        principal: item.product.principal,
        mmp: item.mmp,
        sum: item.sum,
        total: item.totalAmount,
      }));
      return {
        ...state,
        mmpsByGroup: {
          ...state.mmpsByGroup,
          [action.data.maturityPeriod]: mmpsByGroup,
        },
        isLoadingMMP: false,
      };
    }

    case actionTypes.GET_MMPS_BY_GROUP_FAIL: {
      const { status } = action.error;
      return {
        ...state,
        isLoadingMMP: false,
        isServiceUnavailable: (status >= 500 && status <= 504) || (status === 413),
      };
    }

    case actionTypes.GET_MIGRATION_MMPS:
    case actionTypes.GET_ALL_MMPS_BY_MONTH:
    case actionTypes.GET_MMPS_BY_MONTH: {
      return {
        ...state,
        isLoadingMMPbyMonth: true,
      };
    }

    case actionTypes.GET_MMPS_BY_MONTH_SUCCESS: {
      const mmps = action.result.map((item) => {
        const data = flatten(item.data, { maxDepth: 2 });
        return {
          maturityPeriod: item.config.maturityPeriod,
          data,
        };
      });
      const nextState = {
        ...state,
        isLoadingMMPbyMonth: false,
        mmpsByMonth: mmps,
      };
      if (COUNTRY === 'EE') {
        nextState.isContentLoading = false;
        if (state.selectedProduct.fullMaturityMmp >= 5000) {
          const selectedMaturity = state.selectedProduct.maturityPeriods;
          const selectedMmps = mmps
            .find(({ maturityPeriod }) => maturityPeriod === selectedMaturity).data;
          const largestFirstDrawBeforeMmpThreshold = Object.values(selectedMmps).reverse()
            .find(({ mmp }) => mmp < 5000).sum;
          nextState.product.firstDrawAmount = largestFirstDrawBeforeMmpThreshold;
        }
      }
      if (COUNTRY === 'CZ') {
        const mmpsForSelectedProduct = mmps.find(
          // eslint-disable-next-line max-len
          (mmpByMonth) => (mmpByMonth.maturityPeriod === state.selectedProduct.maturityPeriods)
        );
        nextState.availableSumsIndex = Object.values(mmpsForSelectedProduct.data).map(
          (mmp) => mmp.sum
        ).sort((a, b) => (a - b));
        nextState.availableSumsIndex.unshift(0);
      }
      return nextState;
    }

    case actionTypes.GET_ALL_MMPS_BY_MONTH_SUCCESS: {
      const mmpsByMonth = action.result.data.map((item, index) => ({
        data: flatten(item, { maxDepth: 2 }),
        maturityPeriod: index + 1,
      }));
      return {
        ...state,
        isLoadingMMPbyMonth: false,
        mmpsByMonth,
      };
    }

    case actionTypes.GET_MIGRATION_MMPS_FAIL:
    case actionTypes.GET_ALL_MMPS_BY_MONTH_FAIL:
    case actionTypes.GET_MMPS_BY_MONTH_FAIL: {
      return {
        ...state,
        isLoadingMMPbyMonth: false,
      };
    }

    case actionTypes.GET_IL_PRODUCTS: {
      return {
        ...state,
        isLoadingMMP: true,
      };
    }

    case actionTypes.GET_IL_PRODUCTS_SUCCESS: {
      const mmps = filter(action.result.data, (item) => item.maturityPeriods === 36);
      const nextState = {
        ...state,
        isLoadingMMP: false,
        mmps,
      };
      if (COUNTRY === 'LT' || COUNTRY === 'AU') {
        nextState.availablePrincipalsIndex = uniqBy(
          mmps,
          (item) => item.principal
        ).map(
          (item) => item.principal
        );
      }
      return nextState;
    }

    case actionTypes.GET_IL_PRODUCTS_FAIL: {
      return {
        ...state,
        isLoadingMMP: false,
      };
    }

    case actionTypes.PL_VERIFY:
    case actionTypes.ONE_CENT:
    case actionTypes.LOANSUBMITTED:
    case actionTypes.FINISH_FINPASS:
      return {
        ...state,
        isLoanSubmitted: true,
      };

    case actionTypes.INSTANTOR: {
      return {
        ...state,
        currentStep: state.currentStep + 1,
        verifyOptions: 'instantor',
      };
    }

    case actionTypes.INSTANTOR_FAILED: {
      return {
        ...state,
        instantor: {
          ...state.instantor,
          status: 'failed',
        },
      };
    }

    case actionTypes.INSTANTOR_SUCCESS: {
      return {
        ...state,
        instantor: {
          ...state.instantor,
          status: 'success',
        },
      };
    }

    case actionTypes.VERIFY: {
      let nextStep = state.steps.findIndex((item) => item.type === 'INSTANTOR');
      if (action.data.verifyOptions === 'onecent') {
        nextStep = state.steps.findIndex((item) => item.type === 'ONE_CENT');
      }
      return {
        ...state,
        currentStep: nextStep,
        verifyOptions: action.data.verifyOptions,
      };
    }

    case actionTypes.PARTNER_IFRAME_MESSAGE_RECEIVED: {
      const { params } = action.data;
      const { rejectionRedirectUrl } = params;
      const { product } = state;
      let principal = product.principal;
      if (isInteger(parseInt(params.price, 10))) {
        const price = toCents(params.price);
        principal = getInitialProductPrincipal(price, product);
      }
      return {
        ...state,
        product: {
          ...product,
          principal,
        },
        financialData: {
          ...state.financialData,
          channel: `partner_${params.partnerID.toLowerCase()}_loan_app`,
        },
        rejectionRedirectUrl,
      };
    }

    case actionTypes.GO_TO_BANK_STATEMENT: {
      if (COUNTRY === 'ES') {
        return {
          ...state,
          currentStep: state.currentStep + 1,
        };
      }
      return {
        ...state,
        currentStep: state.steps.findIndex((item) => item.type === 'BANK_STATEMENT'),
      };
    }

    case actionTypes.BACK_TO_INCOME: {
      return {
        ...state,
        rejected: null,
      };
    }

    case actionTypes.BACK_TO_VERIFICATION: {
      return {
        ...state,
        isLoanSubmitted: false,
      };
    }

    case actionTypes.BANK_NAME: {
      return {
        ...state,
        bankName: action.data,
      };
    }

    case actionTypes.GET_REGISTRATION_STATUS_SUCCESS: {
      return {
        ...stateWithContentLoading(state, false),
        registrationStatus: action.result.data,
      };
    }

    case actionTypes.SET_INITIAL_LOAN_DATA_EE: {
      const {
        personalDataConsent,
        marketingPermission,
      } = action.data;
      return {
        ...state,
        personalDataConsent,
        marketingPermission,
      };
    }

    case actionTypes.SET_LOAN_PURPOSE: {
      const {
        loanPurpose,
        loanPurposeOther,
      } = action.data;
      const financialData = {
        ...state.financialData,
        loanPurpose,
      };
      if (loanPurposeOther) {
        financialData.loanPurposeOther = loanPurposeOther;
      }
      return {
        ...state,
        canPutFinancialData: true,
        loanPurpose,
        financialData,
      };
    }

    case actionTypes.TOGGLE_ADDRESS_DETAILS: {
      return {
        ...state,
        areAddressDetailsHidden: !state.areAddressDetailsHidden,
      };
    }

    case actionTypes.INCOME_AU:
    case actionTypes.EXPENSES_AU:
    case actionTypes.EDUCATION_WORK:
      return {
        ...stateWithLoanSubmitting(state, true),
        customer: {
          ...state.customer,
          creditApplication: {
            ...state.customer.creditApplication,
            ...action.data,
          },
        },
      };

    case actionTypes.IDENT_DOCUMENTS:
      return {
        ...stateWithLoanSubmitting(state, true),
        customer: action.data,
      };

    case actionTypes.ADDRESS_AU:
      return {
        ...stateWithLoanSubmitting(state, true),
        customer: {
          ...state.customer,
          ...action.data,
        },
      };

    case actionTypes.ADDRESS_AU_SUCCESS:
    case actionTypes.IDENT_DOCUMENTS_SUCCESS:
    case actionTypes.FAMILY_AU_SUCCESS:
    case actionTypes.EDUCATION_WORK_SUCCESS:
    case actionTypes.INCOME_AU_SUCCESS:
    case actionTypes.EXPENSES_AU_SUCCESS:
    case actionTypes.LOANS_AU_SUCCESS:
    case actionTypes.PUT_CREDENTIALS_SUCCESS:
    case actionTypes.PUT_MARITAL_STATUS_PL_SUCCESS:
    case actionTypes.SKIP_CALMA:
    case actionTypes.STEP_FORWARD:
    case actionTypes.INIT_APPLICATION_SCORING_SUCCESS:
      return stateForNextStepWithLoanSubmitting(state);

    case actionTypes.POST_LOAN_PURPOSES_SUCCESS: {
      const isSACC = state.shouldShowSACCPage;

      if (isSACC) {
        return {
          ...stateForNextStepWithLoanSubmitting(state),
        };
      }
      return {
        ...stateWithLoanSubmitting(state, false),
        currentStep: getIndexStepByName(state.steps, STEP_SUBMIT.type),
      };
    }

    case actionTypes.SIGNUP_IMPERSONATION_COMPLETE_SUCCESS: {
      if (COUNTRY === 'AU' && state.skipProvisioAU) {
        const nextStep = getIndexStepByName(state.steps, 'ADDRESS_AU') - 1;
        return stateForNextStepWithLoanSubmitting({ ...state, currentStep: nextStep });
      }

      return stateForNextStepWithLoanSubmitting(state);
    }
    case actionTypes.SKIP_LIBERTO: {
      const nextState = {
        ...stateWithContentLoading(state, false),
      };

      if (COUNTRY === 'ES') {
        nextState.currentStep += 1;
      }

      return nextState;
    }

    case actionTypes.LIBERTO_FAIL: {
      return {
        ...stateWithContentLoading(state, false),
      };
    }

    case actionTypes.GET_OFFER_PRODUCTS_AU_SUCCESS: {
      const { productType } = action.data;
      const productOffer = action.result.data;
      if (INCREASE_APP) {
        if (productType === 'INSTALLMENT') {
          const sortNumbersAscending = (a, b) => a - b;

          const availablePrincipalsIndex = productOffer.products
            .map((item) => parseInt(item.receivedAmount, 10))
            .sort(sortNumbersAscending);

          const products = productOffer.products.map((item) => {
            const availableMaturityPeriods = item.productOffers
              .map((offer) => parseInt(offer.monthsLoanDuration, 10))
              .sort(sortNumbersAscending);

            const product = {
              ...item,
              availableMaturityPeriods,
              productOffers: item.productOffers.reduce((productOffers, offer) => ({
                ...productOffers,
                [offer.monthsLoanDuration]: {
                  ...offer,
                  monthsLoanDuration: parseInt(offer.monthsLoanDuration, 10),
                },
              }), {}),
            };

            return product;
          });

          return {
            ...stateWithContentLoading(state, false),
            productType,
            upsell: {
              ...productOffer,
              availablePrincipalsIndex,
              products,
            },
          };
        }
        if (productType === 'CREDIT_LINE') {
          return {
            ...stateWithContentLoading(state, false),
            productType,
            upgrade: {
              ...productOffer,
              products: productOffer.products.reverse(),
            },
          };
        }
      }
      const availableProducts = parseProductsAU(productOffer.products, productType);
      const availablePrincipalsIndex = Object.keys(availableProducts).sort((a, b) => (a - b));
      if (productType === 'INSTALLMENT' && availablePrincipalsIndex.length === 0) {
        return {
          ...stateWithContentLoading(state, false),
          productType,
          rejected: 'default',
        };
      }
      return {
        ...stateWithContentLoading(state, false),
        productType,
        availableProducts,
        availablePrincipalsIndex,
        paymentInterval: productOffer.paymentInterval,
      };
    }

    case actionTypes.GET_LEGAL_DOCUMENT_SUCCESS: {
      const { data } = action.result;
      return {
        ...stateWithContentLoading(state, false),
        legalDocument: {
          customerName: `${data.firstName} ${data.lastName}`,
          consents: Object.keys(data.consent),
          payload: b64DecodeUnicode(data.document.payload),
          productType: data.productType,
        },
      };
    }

    case actionTypes.POST_REGISTRATION_DETAILS_SUCCESS:
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        customer: {
          ...state.customer,
          ...action.data,
        },
      };

    case actionTypes.SET_LOAN_FORM_FOOTER_VISIBILITY: {
      return {
        ...state,
        isLoanFormFooterVisible: action.data,
      };
    }

    case actionTypes.PUT_UPDATE_LIVING_ADDRESS_SUCCESS: {
      const { isExternalRegistriesFetched } = state;
      return {
        ...state,
        isLoanSubmitting: !isExternalRegistriesFetched,
        currentStep: state.currentStep + (isExternalRegistriesFetched ? 1 : 0),
        financialData: {
          ...state.financialData,
          residence: action.data.residentialStatus,
        },
      };
    }

    case actionTypes.PUT_ADDRESS_INFO_SUCCESS: {
      const { authentication: { authenticationMethod } } = store.getState();
      return {
        ...state,
        isLoanSubmitting: false,
        currentStep: state.currentStep + 1,
        financialData: {
          ...state.financialData,
          residence: action.data.residentialStatus,
          channel: authenticationMethod === 'IMPERSONATION' ? action.data.channel : state.financialData.channel,
        },
      };
    }

    case actionTypes.PUT_EDUCATION_INFO: {
      return {
        ...state,
        isLoanSubmitting: true,
      };
    }

    case actionTypes.PUT_EDUCATION_INFO_SUCCESS: {
      return {
        ...state,
        financialData: {
          ...state.financialData,
          education: action.data.education,
        },
      };
    }

    case actionTypes.PUT_EDUCATION_INFO_FAIL: {
      return {
        ...state,
        isLoanSubmitting: false,
      };
    }

    case actionTypes.START_CUSTOMER_IDENTIFICATION_SUCCESS: {
      return {
        ...state,
      };
    }

    case actionTypes.PUT_SCREEN_NAME_SUCCESS_AFTER_EDUCATION_LT: {
      return {
        ...state,
        isLoanSubmitting: false,
        currentStep: state.currentStep + 1,
      };
    }

    case actionTypes.FETCH_EXTERNAL_REGISTRIES_SUCCESS: {
      const { financialData } = action.result.data;
      const isClientMarried = financialData.maritalStatus === 'MARRIED';
      let steps = state.steps;
      if (!isClientMarried) {
        steps = steps.filter((step) => step.type !== 'LOAN_TYPE_LT');
      }
      return {
        ...state,
        steps,
        isClientMarried,
        // isLoanSubmitting: false,
        // currentStep: state.currentStep + 1,
        isExternalRegistriesFetched: true,
      };
    }

    case actionTypes.REGISTRIES_LT: {
      return {
        ...state,
        currentStep: state.currentStep + 1,
        checkExternalPolicies: false,
      };
    }

    case actionTypes.CHECK_EXTERNAL_POLICIES_SUCCESS: {
      const {
        isMarried, residenceDocumentType, status, rejectReason,
      } = action.result.data;
      const isClientMarried = isMarried === true;
      // eslint-disable-next-line max-len
      const hasPermanentResidencePermit = residenceDocumentType === residenceDocumentTypes.PERMANENT;
      let steps = state.steps;
      if (!isClientMarried) {
        steps = steps.filter((step) => step.type !== 'LOAN_TYPE_LT');
      }
      if (!hasPermanentResidencePermit) {
        steps = steps.filter((step) => step.type !== 'NATIONALITY_LT');
      }
      if (status === 'REJECTED') {
        return {
          ...state,
          isLoanSubmitting: false,
          state: status,
          rejected: rejectReason,
          rejectedMessage: rejectReason,
        };
      }
      return {
        ...state,
        steps,
        isClientMarried,
        hasPermanentResidencePermit,
        isLoading: false,
        checkExternalPolicies: true,
        isExternalRegistriesFetched: true,
      };
    }

    case actionTypes.GET_DOCUMENT_TYPE_SUCCESS: {
      const { residenceDocumentType } = action.result.data;
      // eslint-disable-next-line max-len
      const hasPermanentResidenceStatus = residenceDocumentType === residenceDocumentTypes.PERMANENT;
      let steps = state.steps;
      if (!hasPermanentResidenceStatus) {
        steps = steps.filter((step) => step.type !== 'NATIONALITY_LT');
      }
      return {
        ...state,
        steps,
        isLoanSubmitting: false,
      };
    }

    case actionTypes.GET_SALARY_DATA_SUCCESS: {
      const { income } = action.result.data;
      return {
        ...state,
        sodraIncome: income ? toCents(income, 'float') : income,
      };
    }

    case actionTypes.VAS_CONFIRM_SUCCESS: {
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        isMedicareConfirmed: state.medicare && state.medicare.enabled,
        isBillProtectorConfirmed: state.billProtector && state.billProtector.enabled,
      };
    }

    case actionTypes.LIABILITIES_LT_SUCCESS: {
      const { repayments, refinanceRequest } = action.data;
      const monthlyObligations = toCents(repayments);
      return {
        ...state,
        financialData: {
          ...state.financialData,
          monthlyObligations,
        },
        refinance: refinanceRequest,
        canPutFinancialData: true,
      };
    }

    case actionTypes.POST_LIBERTO_TOKEN: {
      return {
        ...state,
        libertoData: {},
      };
    }

    case actionTypes.POST_LIBERTO_TOKEN_SUCCESS: {
      const { token, url } = action.result.data;
      return {
        ...state,
        libertoData: {
          token,
          url,
        },
      };
    }

    case actionTypes.SAVE_LIBERTO_SUCCESS: {
      return {
        ...state,
        libertoSaved: true,
      };
    }

    case actionTypes.GET_MX_BANKS_SUCCESS: {
      const mxBanks = sortBy(action.result.data, 'bankShortName').map((bank) => ({
        title: bank.bankShortName,
        type: bank.bankCode,
      }));
      return {
        ...state,
        mxBanks,
      };
    }

    case actionTypes.SHOW_OTP_SECTION: {
      return {
        ...state,
        otpSectionVisible: true,
      };
    }

    case actionTypes.HIDE_OTP_SECTION: {
      return {
        ...state,
        otpSectionVisible: false,
      };
    }

    case actionTypes.INIT_OTP:
      return {
        ...state,
        pinValidationRequired: false,
        isPinValidationLoading: true,
      };

    case actionTypes.INIT_OTP_SUCCESS:
      return {
        ...state,
        pinValidationRequired: true,
        isPinValidationLoading: false,
      };

    case actionTypes.INIT_OTP_FAIL:
      return {
        ...state,
        isPinValidationLoading: false,
        pinValidationRequired: false,
      };

    case actionTypes.VALIDATE_OTP:
      return {
        ...state,
        isPinValidationLoading: true,
      };

    case actionTypes.VALIDATE_OTP_FAIL:
    case actionTypes.VALIDATE_OTP_SUCCESS: {
      const isSuccess = action.type === actionTypes.VALIDATE_OTP_SUCCESS;
      let nextStep = state.currentStep;
      if (COUNTRY === 'ES' && isSuccess) {
        nextStep += 1;
      }
      return {
        ...state,
        isPinValidationLoading: false,
        currentStep: nextStep,
        otpSectionVisible: !isSuccess,
        pinValidationRequired: !isSuccess,
      };
    }

    case actionTypes.GET_CHANNEL_LIST_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        channelList: action.result.data.map(
          (channel) => ({
            type: channel.id,
            title: channel.name,
          })
        ),
      };

    case actionTypes.GET_BANK_ACCOUNT_SUCCESS: {
      const { data: { bankAccount } } = action.result;
      const bsb = bankAccount.substring(0, 6);
      const accountNumber = bankAccount.substring(6);
      return {
        ...state,
        bsb,
        accountNumber,
      };
    }

    case actionTypes.GET_FINPASS_BANKS_SUCCESS: {
      const allBanks = action.result.banks.map(
        (finpassBank) => ({
          ...finpassBank,
          value: finpassToAioBanksLT[finpassBank.code],
        })
      );
      let swed = [];
      const banksWithoutSwed = allBanks.filter((bank) => {
        const isSwed = bank.value === 'swed';
        if (isSwed) {
          swed = [bank];
        }
        return !isSwed;
      });
      return {
        ...stateWithContentLoading(state, false),
        finpassToken: action.result.token,
        finpassBanks: [
          ...swed,
          ...banksWithoutSwed,
        ],
      };
    }

    case actionTypes.GET_FINPASS_REDIRECT_URL:
      return {
        ...stateWithContentLoading(state, true),
        finpassRedirectUrl: null,
      };

    case actionTypes.GET_FINPASS_REDIRECT_URL_SUCCESS: {
      const { data } = action.result;
      return {
        ...stateWithContentLoading(state, false),
        finpassRedirectUrl: data.redirectUrl,
        finpassMessageId: data.msgId,
      };
    }

    case actionTypes.GET_PROVISO_IFRAME_URL_SUCCESS:
      return {
        ...stateWithContentLoading(state, false),
        provisoUrl: action.result.data.iframeUrl,
      };

    case actionTypes.RESET_BANK_SCRAPING_RESULT:
      return {
        ...state,
        bankScrapingResult: {},
      };

    case actionTypes.GET_AUTO_APPROVAL_STATUS:
      return {
        ...state,
        isAutoApprovalStatusLoading: true,
      };

    case actionTypes.GET_AUTO_APPROVAL_STATUS_SUCCESS: {
      const {
        data: { appPassedAutoApproveConditions, maxAutoApprovalProduct, externalNetIncome },
      } = action.result;
      return {
        ...state,
        isAutoApprovalStatusLoading: false,
        appPassedAutoApproveConditions,
        maxAutoApprovalProduct,
        externalNetIncome,
      };
    }

    case actionTypes.GET_AUTO_APPROVAL_STATUS_FAIL:
      return {
        ...state,
        isAutoApprovalStatusLoading: false,
      };

    case actionTypes.PUT_CONTRACT_DETAILS_SUCCESS: {
      let paymentDayData = { dueDate: state.loanDueDateRange.defaultDate };
      // eslint-disable-next-line max-len
      const paymentDateResult = action.result.filter((item) => item.config.isPaymentDateRequest).shift();
      if (paymentDateResult && paymentDateResult.config.method === 'put') {
        paymentDayData = JSON.parse(paymentDateResult.config.data);
      }

      const { bankAccount } = action.data;

      return {
        ...stateForNextStepWithLoanSubmitting(state),
        ...paymentDayData,
        customer: {
          ...state.customer,
          bankAccount,
        },
      };
    }

    case actionTypes.PUT_NORDIGEN_CONSENT_SUCCESS:
      return {
        ...stateForNextStepWithLoanSubmitting(state),
      };

    case actionTypes.PUT_NORDIGEN_REQUEST_ID_SUCCESS:
      return {
        ...stateWithLoanSubmitting(state),
        nordigenSuccess: true,
        isLoanSubmitted: true,
      };

    case actionTypes.GET_FEES_SUCCESS:
      return stateWithContentLoading({
        ...state,
        fees: {
          ...state.fees,
          ...action.result.data,
        },
      }, false);

    case actionTypes.SET_GUARANTOR_TYPE:
      return {
        ...state,
        financialData: {
          ...state.financialData,
          guarantorType: action.data,
        },
      };

    case actionTypes.SET_RESCORING_TO_DO_FOR_OPEN_CREDIT_APPLICATION:
      return {
        ...state,
        rescoringToDoForOpenCreditApplication: action.data,
      };

    case actionTypes.SET_STEPS:
      return {
        ...state,
        steps: action.data,
      };

    case actionTypes.SET_LAST_STATUS_PAGE:
      return {
        ...state,
        lastStatusPage: action.data,
      };

    case actionTypes.SET_REJECTION:
      return {
        ...state,
        ...stateAfterRejection(action.data),
      };

    case actionTypes.TOGGLE_CONFIRMATION_BUTTON_VISIBILITY:
      return {
        ...state,
        hiddenConfirmationButton: action.data,
      };

    case actionTypes.GET_MIGRATION_PRODUCTS: {
      return {
        ...stateWithContentLoading(state, true),
        showCalculator: false,
        isWaitingForOffer: true,
        isLoanFormFooterVisible: false,
      };
    }

    case actionTypes.GET_CONTRACT_FAIL:
    case actionTypes.POST_MIGRATION_SCORING_ES_FAIL:
    case actionTypes.GET_MIGRATION_SCORING_ES_FAIL: {
      return {
        ...state,
        rejected: 'default',
        isWaitingForOffer: false,
        finalState: true,
        technicalError: true,
      };
    }

    case actionTypes.GET_MIGRATION_PRODUCTS_SUCCESS: {
      const products = sortBy(action.result.data, 'principal').reverse();

      if (products.length === 0) {
        return {
          ...state,
          rejected: 'default',
          isLoanSubmitting: false,
          isWaitingForOffer: false,
          finalState: true,
        };
      }

      return {
        ...stateWithContentLoading(state, true),
        isWaitingForOffer: true,
        isLoanFormFooterVisible: true,
        products: products.reduce((productMap, product) => ({
          ...productMap,
          [product.principal]: {
            ...product,
          },
        }), {}),
      };
    }

    case actionTypes.GET_MIGRATION_SCORING_ES_SUCCESS: {
      const { currentStep } = action.result.data;
      const {
        statusMaxTimeout,
        waitingForOfferInterval,
        statusCheckAttepts,
      } = state;
      let nextState = {
        ...stateWithContentLoading(state, true),
        technicalError: false,
        statusCheckAttepts: state.statusCheckAttepts + 1,
      };

      const isFinished = currentStep === 'FINISHED';
      const isRejected = currentStep === 'REJECTED';
      const isFailed = currentStep === 'FAILED';

      if (isRejected) {
        nextState = {
          ...nextState,
          rejected: 'default',
          finalState: true,
          isWaitingForOffer: false,
        };
      } else if (isFinished) {
        nextState.statusCheckAttepts = 0;
        nextState.finalState = true;
        nextState.isWaitingForOffer = false;
      } else if (isFailed) {
        nextState.finalState = true;
        nextState.statusCheckAttepts = 0;
        nextState.technicalError = true;
      }

      if ((statusMaxTimeout / waitingForOfferInterval) < statusCheckAttepts) {
        nextState = {
          ...nextState,
          rejected: 'default',
          isWaitingForOffer: false,
          finalState: true,
          technicalError: true,
        };
      }

      return nextState;
    }

    case actionTypes.GET_CONTRACT_SUCCESS: {
      return {
        ...stateWithContentLoading(state, false),
        contract: {
          ...action.result.data,
        },
      };
    }

    case actionTypes.PUT_MIGRATION_PRODUCT_SUCCESS: {
      return {
        ...stateForNextStepWithLoanSubmitting(state),
        selectedProduct: action.data.product,
        product: {
          ...state.product,
          firstDrawAmount: state.product.principal,
        },
      };
    }

    case actionTypes.GET_MIGRATION_MMPS_SUCCESS: {
      const { data } = action.result;
      const { products = [] } = state;
      const mmpDataValues = Object.values(data);

      if (products.length) {
        Object.keys(products).forEach((principal) => {
          const mmpElement = mmpDataValues.find(
            (element) => element.product.principal === parseInt(principal, 10)
          );
          products[principal] = {
            ...products[principal],
            mmp: mmpElement.mmp,
            sum: mmpElement.sum,
            guaranteeFee: mmpElement.guaranteeFee,
            mmpPlusGuaranteeFee: mmpElement.mmpPlusGuaranteeFee,
            totalAmount: mmpElement.totalAmount,
          };
        });
      } else {
        Object.values(data).forEach((element) => {
          const { product } = element;
          const { principal } = product;

          products[principal] = {
            ...product,
            mmp: element.mmp,
            sum: element.sum,
            guaranteeFee: element.guaranteeFee,
            mmpPlusGuaranteeFee: element.mmpPlusGuaranteeFee,
            totalAmount: element.totalAmount,
          };
        });
      }

      return {
        ...state,
        products: {
          ...products,
        },
        isLoadingMMPbyMonth: false,
      };
    }

    case actionTypes.SET_LOAN_SUBMITTING: {
      return {
        ...state,
        isLoanSubmitting: true,
      };
    }

    case actionTypes.UNSET_LOAN_SUBMITTING: {
      return {
        ...state,
        isLoanSubmitting: false,
      };
    }

    case actionTypes.GET_NORDIGEN_BANKS_SUCCESS: {
      const sanbox = {
        id: 'SANDBOXFINANCE_SFIN0000',
        name: 'SANBOX',
        bic: 'SANBOX',
      };

      const nordigenBankList = action.result.data;

      if (!PRODUCTION) {
        nordigenBankList.push(sanbox);
      }

      return {
        ...state,
        isLoanSubmitting: false,
        isContentLoading: false,
        showNordigenError: false,
        nordigenBankList,
      };
    }

    case actionTypes.SET_NORDIGEN_BANK: {
      return {
        ...state,
        selectedNordigenBank: action.data,
      };
    }

    case actionTypes.POST_NORDIGEN_BANK_SUCCESS: {
      return {
        ...state,
        chooseNordigenConfirm: true,
        nordigenInitiateLink: action.result.data.initiate,
      };
    }

    case actionTypes.SKIP_NORDIGEN_STEP: {
      return {
        ...state,
        currentStep: state.currentStep + 1,
        skipNordigenConfirm: action.data,
      };
    }

    case actionTypes.SET_NORDIGEN_ERROR: {
      return {
        ...state,
        showNordigenError: action.data,
      };
    }

    case actionTypes.GET_NORDIGEN_ACCOUNT_SUCCESS: {
      // fix for bank validation from Nordigen Sandbox on DEV ENV
      const accounts = action.result.data;

      if (!PRODUCTION) {
        accounts.map((item) => {
          const newObj = item;

          if (item.iban && typeof item.iban === 'string' && item.iban.substring(0, 2) === 'GL') {
            newObj.iban = 'EE874242844068787592';
          }
          return newObj;
        });
      }

      return {
        ...state,
        accountFromNordigen: accounts,
      };
    }

    case actionTypes.SET_REQUESTED_FINANCIAL_DATA: {
      return {
        ...state,
        requestedFinancialData: action.data,
      };
    }

    case actionTypes.SET_PROVISIO_SKIP_AU: {
      return {
        ...state,
        skipProvisioAU: action.data,
      };
    }

    case actionTypes.START_REIDENTIFY_CUSTOMER: {
      const stepIndexToReplace = getIndexStepByName(state.steps, STEP_CUSTOMER.type);
      const updatedSteps = state.steps.map((step, index) => {
        if (index === stepIndexToReplace) {
          return STEP_REIDENTIFY_CUSTOMER;
        }
        return step;
      });

      return {
        ...stateWithLoanSubmitting(state),
        isRequiredReindentification: true,
        currentStep: stepIndexToReplace,
        steps: updatedSteps,
      };
    }

    case actionTypes.REIDENTIFY_CUSTOMER:
    case actionTypes.FINISH_REIDENTIFY_CUSTOMER:
      return stateWithLoanSubmitting(state, true);

    case actionTypes.REIDENTIFY_CUSTOMER_FAIL:
    case actionTypes.FINISH_REIDENTIFY_CUSTOMER_FAIL:
      return stateWithLoanSubmitting(state, false);

    case actionTypes.GO_TO_NEXT_STEP:
      return {
        ...stateWithLoanSubmitting(state),
        currentStep: state.currentStep + 1,
      };

    case actionTypes.CHECK_FOR_TAX_AUTHORITY_DATA: {
      return {
        ...stateWithLoanSubmitting(state, true),
      };
    }

    case actionTypes.CHECK_FOR_TAX_AUTHORITY_DATA_SUCCESS: {
      const { requiredConsentsRedirectUrl } = action.result.data;
      const { steps } = state;
      const nextState = {
        ...stateWithLoanSubmitting(state, false),
        currentStep: steps.findIndex((item) => item.type === STEP_LOAN_PURPOSE.type),
      };
      const consentsClickedBefore = localStorage.getItem(TAX_AUTHORITY_CONSENTS_CLICKED);
      if (!consentsClickedBefore && requiredConsentsRedirectUrl) {
        nextState.currentStep = steps.findIndex((item) => item.type === STEP_SYSTEM_AUTHORITY.type);
        nextState.requiredConsentsRedirectUrl = requiredConsentsRedirectUrl;
      }

      return nextState;
    }

    case actionTypes.CHECK_FOR_TAX_AUTHORITY_DATA_FAIL: {
      const { steps } = state;

      return {
        ...stateWithLoanSubmitting(state, false),
        currentStep: steps.findIndex((item) => item.type === STEP_LOAN_PURPOSE.type),
      };
    }

    case actionTypes.REMOVE_IDENTIFY_VERIFICATION_WAY: {
      return {
        ...state,
        identityVerificationWay: null,
      };
    }

    case actionTypes.GET_FOLLOWUP_QUESTIONS_SUCCESS: {
      const { followUpQuestionsParameters } = action.result.data;

      return {
        ...state,
        followUpQuestions: followUpQuestionsParameters,
        followUpQuestionsLoaded: true,
      };
    }

    case actionTypes.GET_FOLLOWUP_QUESTIONS_FAIL: {
      return {
        ...state,
        followUpQuestionsLoaded: true,
        followUpQuestionsFetchFail: true,
      };
    }

    case actionTypes.PUT_FOLLOWUP_QUESTIONS_SUCCESS: {
      router.push(`/success${location.search}${location.hash}`);

      return { ...state };
    }

    case actionTypes.GET_GOV_BENEFITS_VALUE:
    case actionTypes.GET_PRODUCT_NAME: {
      return {
        ...stateWithoutLoading(state),
      };
    }

    case actionTypes.GET_PRODUCT_NAME_SUCCESS: {
      const { product } = action.result.data;
      const isSACC = product.name.includes(PRODUCT_TYPES.SACC);

      return {
        ...stateWithContentLoading(state, false),
        selectedProduct: {
          ...state.selectedProduct,
          isSACC,
        },
      };
    }

    case actionTypes.GET_GOV_BENEFITS_VALUE_SUCCESS: {
      const { BV002 } = action.result.data;
      const isGovBenefits = !!Number(BV002);

      return {
        ...stateWithContentLoading(state, false),
        customer: {
          ...state.customer,
          governmentBenefits: isGovBenefits,
        },
      };
    }

    case actionTypes.SHOULD_SHOW_SACC_PAGE: {
      return {
        ...stateWithContentLoading(state, false),
        shouldShowSACCPage: action.data,
      };
    }

    case actionTypes.GET_CENTERLINK_REPORTS_STATUS: {
      return {
        ...state,
        isCheckingCenterlinkStatus: true,
      };
    }

    case actionTypes.GET_CENTERLINK_REPORTS_STATUS_SUCCESS: {
      const { firstRequestStartDate, maxRequestTime } = action.data;
      const currentDate = new Date();
      const firstRequestTime = currentDate - firstRequestStartDate;
      const { status } = action.result.data;

      if (firstRequestTime >= maxRequestTime || status === CENTERLINK_STATUS.READY) {
        const nextStep = status === CENTERLINK_STATUS.READY
          ? STEP_SUBMIT.type
          : STEP_INFORMATION.type;
        const nextStepIndex = getIndexStepByName(state.steps, nextStep);

        return {
          ...state,
          currentStep: nextStepIndex,
          centerlinkStatus: status,
          isCheckingCenterlinkStatus: false,
        };
      }
      return {
        ...state,
      };
    }

    case actionTypes.GET_CENTERLINK_REPORTS_STATUS_FAIL: {
      const nextStepIndex = getIndexStepByName(state.steps, STEP_INFORMATION.type);

      return {
        ...state,
        currentStep: nextStepIndex,
        centerlinkStatus: 'error',
        isCheckingCenterlinkStatus: false,
      };
    }

    case actionTypes.GO_TO_SPECIFIC_STEP: {
      const { steps } = state;
      const { stepType } = action.data;

      return {
        ...state,
        currentStep: getIndexStepByName(steps, stepType),
      };
    }

    case actionTypes.PUT_MISSING_ADDITIONAL_CONSENT_SUCCESS: {
      const { isLoanSubmitted, metadata: { STEP_NAME } } = state;

      if (!isLoanSubmitted) {
        const currentStep = STEP_NAME
          ? getIndexStepByName(state.steps, STEP_NAME) + 1
          : getIndexStepByName(state.steps, 'CREDIT_SENSE');

        return {
          ...state,
          currentStep,
        };
      }

      return { ...state };
    }

    default:
      return state;
  }
}
